import { SQLocal } from "sqlocal";
import { fraLagret } from "@infoflyt/shared/tekst";
import { oektIdFor } from "@infoflyt/shared/logg";
import { dekrypter, krypter } from "./krypto";
import type {
  AuditAction, Deltaker, LogButton, LogEntry, PagePreference, StoredAnchor,
  SuSpoersmaal, SuSvar, Tekstflate, TimeEntry,
} from "./types";

/**
 * Lokal, append-only logg i SQLite.
 *
 * På web ligger databasen i Origin Private File System — utilgjengelig for
 * andre nettsteder og usynlig i Filer.
 *
 * ALL FRITEKST ER KRYPTERT. Loggtekst, markøretiketter, knappenavn, notater på
 * timeføring og revisjonsloggens før- og etterverdier går gjennom AES-GCM med
 * en ikke-eksporterbar nøkkel — se `state/krypto.ts` for hva det beskytter mot
 * og hva det ikke gjør. Krypteringen ligger her, ved databasegrensen, slik at
 * resten av appen ser klartekst og ikke trenger å vite noe.
 *
 * Tidsstempler, tidskodeverdier og id-er står i klartekst. De sorterer og
 * regner, og de er ikke innholdet — det personlige ligger i det regi skriver.
 *
 * OPFS selv har ingen motstykke til SQLCipher. Skal hele filen krypteres, må
 * appen kjøre som binærfil gjennom @capacitor-community/sqlite. Se
 * docs/02-arkitektur.md og docs/09-juridisk.md.
 */

const DB_FILE = "infoflyt.sqlite3";

/**
 * Klientgenerert UUID.
 *
 * `crypto.randomUUID` finnes bare i en sikker kontekst. Den mangler derfor
 * over vanlig http, og et kall som kaster her tar med seg hele appen før den
 * rekker å si hva som er galt. Reservevarianten er ikke kryptografisk, men
 * id-ene skal bare være unike, ikke uforutsigbare.
 */
export function newId(): string {
  if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof crypto?.getRandomValues === "function") crypto.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Databasen opprettes først når den skal åpnes. Konstruktøren starter en
 * worker, og det skal ikke skje på et opphav der OPFS uansett ikke finnes.
 */
let instance: SQLocal | null = null;
function client(): SQLocal {
  instance ??= new SQLocal(DB_FILE);
  return instance;
}
const sql: SQLocal["sql"] = (...args) => client().sql(...args);
const transaction: SQLocal["transaction"] = (fn) => client().transaction(fn);

let opened: Promise<void> | null = null;

/**
 * Kjoerer en spoerring bygd av en streng.
 *
 * sqlocal tar bare tagged templates, saa en streng maa pakkes som en. Dette
 * er stygt og med vilje samlet paa ett sted: migreringene trenger det, fordi
 * tabellnavn og kolonnenavn ikke kan vaere parametre.
 */
function raw(spørring: string): Promise<unknown[]> {
  return sql([spørring] as unknown as TemplateStringsArray);
}

/** Tabellene som faar `production_id`. Se migrering 1. */
const AVGRENSEDE_TABELLER = [
  "tc_anchor", "log_session", "log_entry", "log_button", "time_entry", "audit_log",
] as const;

/**
 * Skjemaversjonen appen forventer. Oekes naar `MIGRERINGER` faar et ledd.
 */
const SKJEMAVERSJON = 21;

/**
 * Skjemaet.
 *
 * Skrives som tagged templates fordi det er det eneste sqlocal tar imot —
 * en streng sendt inn som argument blir ikke tolket som en spørring.
 */
async function migrate(aktivProduksjon: string): Promise<void> {
  await sql`CREATE TABLE IF NOT EXISTS tc_anchor (
    id TEXT PRIMARY KEY, production_id TEXT NOT NULL DEFAULT '',
    shoot_day TEXT NOT NULL, tc_frames INTEGER NOT NULL,
    monotonic_ms REAL NOT NULL, boot_id TEXT NOT NULL, wall_utc INTEGER NOT NULL,
    framerate TEXT NOT NULL, drop_frame INTEGER NOT NULL, source TEXT NOT NULL,
    derived_from TEXT, kind TEXT NOT NULL DEFAULT 'klokke',
    jammet_utc INTEGER)`;

  await sql`CREATE TABLE IF NOT EXISTS log_entry (
    id TEXT PRIMARY KEY, production_id TEXT NOT NULL DEFAULT '',
    session_id TEXT NOT NULL, anchor_id TEXT NOT NULL,
    monotonic_ms REAL NOT NULL, boot_id TEXT NOT NULL, wall_utc INTEGER NOT NULL,
    button_id TEXT, label TEXT NOT NULL DEFAULT '', color TEXT,
    text TEXT NOT NULL DEFAULT '', tc_state TEXT NOT NULL, deleted_at INTEGER,
    participant_id TEXT, skrevet_av TEXT)`;

  // Sortering går alltid på veggklokke. Tidskode er ikke sorterbar over
  // midnatt, og kolonnen finnes uansett ikke.
  await sql`CREATE INDEX IF NOT EXISTS log_entry_wall ON log_entry (session_id, wall_utc)`;
  await sql`CREATE INDEX IF NOT EXISTS log_entry_anchor ON log_entry (anchor_id)`;

  await sql`CREATE TABLE IF NOT EXISTS log_session (
    id TEXT PRIMARY KEY, production_id TEXT NOT NULL DEFAULT '',
    shoot_day TEXT NOT NULL, status TEXT NOT NULL,
    opened_at INTEGER NOT NULL, closed_at INTEGER)`;

  /*
   * Deltakerne og SU-/synkspoersmaalene.
   *
   * Begge er produksjonens, ikke brukerens, og hentes fra Supabase — se
   * state/su.ts. De ligger lokalt fordi appen er offline-foerst: en synk i
   * en kjeller skal ha lista si uten dekning.
   *
   * `deltaker_id` NULL paa et spoersmaal betyr GENERELT. Det stilles til
   * alle, og ligger oeverst paa hver deltakers liste.
   */
  await sql`CREATE TABLE IF NOT EXISTS participant (
    id TEXT PRIMARY KEY, production_id TEXT NOT NULL,
    name TEXT NOT NULL, abbreviation TEXT NOT NULL, sort_order INTEGER NOT NULL DEFAULT 0)`;
  await sql`CREATE INDEX IF NOT EXISTS participant_prod ON participant (production_id)`;

  await sql`CREATE TABLE IF NOT EXISTS su_question (
    id TEXT PRIMARY KEY, production_id TEXT NOT NULL,
    participant_id TEXT, text TEXT NOT NULL, sort_order INTEGER NOT NULL DEFAULT 0,
    kilde_entry TEXT, kilde_frames INTEGER, kilde_rate TEXT, kilde_df INTEGER,
    kilde_dag TEXT, kilde_tekst TEXT, kilde_rapport INTEGER,
    skrevet INTEGER, shoot_day TEXT)`;
  await sql`CREATE INDEX IF NOT EXISTS su_question_prod ON su_question (production_id)`;

  /*
   * Ett avhuket spoersmaal. Peker paa SYNK-posten det ble skrevet inn i.
   *
   * Lever ikke posten, er spoersmaalet ustilt — det er hele koblingen, og
   * den gjoer at haken og loggen ikke kan komme i utakt.
   */
  await sql`CREATE TABLE IF NOT EXISTS su_answer (
    id TEXT PRIMARY KEY, production_id TEXT NOT NULL, entry_id TEXT NOT NULL,
    question_id TEXT NOT NULL, participant_id TEXT NOT NULL, at_utc INTEGER NOT NULL,
    answer TEXT NOT NULL DEFAULT '', answer_at INTEGER)`;
  await sql`CREATE INDEX IF NOT EXISTS su_answer_entry ON su_answer (entry_id)`;
  await sql`CREATE INDEX IF NOT EXISTS su_answer_prod ON su_answer (production_id)`;

  /*
   * HVA SOM ER STILT, AV HVEM SOM HELST.
   *
   * Et spoersmaal som er stilt er stilt for alle — avgjort 25.10.2026. Har
   * reporteren spurt ALI om noe fra nettflata, skal det ikke staa paa regis
   * liste; da faar deltakeren samme spoersmaal to ganger.
   *
   * EN REN HURTIGBUFFER, og derfor en egen tabell. `su_answer` er appens
   * EGNE haker: de baerer posten, tidskoden og svaret, og de ligger i
   * utboksen. Denne baerer bare paret, byttes helt ved hver synk, og kan
   * kastes uten at noe gaar tapt. To ulike livsloep hoerer ikke i samme
   * tabell — en replace-all paa `su_answer` ville slettet haker som ennaa
   * ikke var sendt.
   */
  await sql`CREATE TABLE IF NOT EXISTS su_stilt (
    production_id TEXT NOT NULL, question_id TEXT NOT NULL,
    participant_id TEXT NOT NULL,
    /* Hvem som stilte det, og hvilken opptaksdag. Rapporten skiller «stilt
       av Lars, D6» fra «ikke stilt enda» — se packages/shared/rapport. */
    hvem TEXT, dag TEXT,
    PRIMARY KEY (production_id, question_id, participant_id))`;

  /*
   * Tekstflater: brief for teknisk crew og det som maatte komme.
   *
   * SKRIVES paa nettflata, LESES her. Ulikt dagsplanen, som kommer ferdig
   * satt som PDF — en tekst kan rettes med to ord, leses med telefonens egen
   * skriftstoerrelse og veier ingenting aa laste ned.
   *
   * Staar i klartekst, som `document_cache`. Krypteringen i denne fila
   * gjelder fritekst regi selv har skrevet om deltakere; dette er beskjeder
   * til crewet, og PDF-ene ligger uansett ukryptert i OPFS.
   */
  await sql`CREATE TABLE IF NOT EXISTS text_page (
    id TEXT PRIMARY KEY, production_id TEXT NOT NULL, slug TEXT NOT NULL,
    title TEXT NOT NULL, content TEXT NOT NULL DEFAULT '[]', updated_at TEXT NOT NULL)`;
  await sql`CREATE UNIQUE INDEX IF NOT EXISTS text_page_slug
    ON text_page (production_id, slug)`;

  /*
   * Utboksen.
   *
   * Appen har til naa bare LEST delt tilstand. Huskepunktene snur det: man
   * huker av i en kjeller, og det skal fram naar dekningen kommer.
   *
   * Raden sier bare HVILKEN side som maa sendes, ikke hva. Innholdet leses
   * fra `text_page` i det den sendes — da samler ti raske endringer seg til
   * én sending, og det som gaar opp er alltid det som faktisk staar.
   */
  await sql`CREATE TABLE IF NOT EXISTS utboks (
    production_id TEXT NOT NULL, slug TEXT NOT NULL, laget INTEGER NOT NULL,
    PRIMARY KEY (production_id, slug))`;

  /*
   * Shotlista: scener og bilder.
   *
   * Egne tabeller, ikke en tekstflate. Et bilde kan ha en kommentar og et
   * referansebilde, og data per linje er nettopp det et tekstdokument ikke
   * kan baere. Settes opp paa nettflata; herfra hukes den av.
   */
  await sql`CREATE TABLE IF NOT EXISTS shot_scene (
    id TEXT PRIMARY KEY, production_id TEXT NOT NULL, shoot_day TEXT NOT NULL,
    name TEXT NOT NULL, sort_order INTEGER NOT NULL DEFAULT 0)`;

  await sql`CREATE TABLE IF NOT EXISTS shot (
    id TEXT PRIMARY KEY, scene_id TEXT NOT NULL, text TEXT NOT NULL,
    note TEXT NOT NULL DEFAULT '', image_path TEXT,
    done INTEGER NOT NULL DEFAULT 0, sort_order INTEGER NOT NULL DEFAULT 0)`;

  /*
   * Utboks for haker paa shot. Egen tabell og ikke `utboks`: den sender en
   * SIDE, denne sender én rad. Produksjonen staar med, slik at sletting paa
   * `end_date` kan treffe én produksjon og ikke alle.
   */
  await sql`CREATE TABLE IF NOT EXISTS utboks_shot (
    shot_id TEXT PRIMARY KEY, production_id TEXT NOT NULL, laget INTEGER NOT NULL)`;

  await sql`CREATE TABLE IF NOT EXISTS log_button (
    id TEXT PRIMARY KEY, production_id TEXT NOT NULL DEFAULT '',
    label TEXT NOT NULL, color TEXT NOT NULL,
    sort_order INTEGER NOT NULL)`;

  await sql`CREATE TABLE IF NOT EXISTS user_page_preference (
    production_id TEXT NOT NULL, page TEXT NOT NULL,
    enabled INTEGER NOT NULL, sort_order INTEGER NOT NULL,
    PRIMARY KEY (production_id, page))`;

  await sql`CREATE TABLE IF NOT EXISTS time_entry (
    id TEXT PRIMARY KEY, production_id TEXT NOT NULL DEFAULT '',
    started_at INTEGER NOT NULL, stopped_at INTEGER,
    note TEXT NOT NULL DEFAULT '', source TEXT NOT NULL,
    monotonic_ms REAL, boot_id TEXT)`;

  /*
   * Lagt til etter at tabellen fantes. Forløpt tid skal regnes på monoton
   * klokke, ikke på veggklokka — se TimeEntry i types.ts.
   *
   * ALTER kastes om kolonnen alt er der, og det er den normale tilstanden.
   * SQLite har ingen IF NOT EXISTS for kolonner, så feilen svelges bevisst.
   */
  for (const kolonne of ["monotonic_ms REAL", "boot_id TEXT"]) {
    try {
      await sql([`ALTER TABLE time_entry ADD COLUMN ${kolonne}`] as unknown as TemplateStringsArray);
    } catch { /* kolonnen fantes */ }
  }

  await sql`CREATE TABLE IF NOT EXISTS audit_log (
    id TEXT PRIMARY KEY, production_id TEXT NOT NULL DEFAULT '',
    entity_type TEXT NOT NULL, entity_id TEXT NOT NULL,
    action TEXT NOT NULL, before TEXT, after TEXT, created_at INTEGER NOT NULL)`;

  /*
   * Dokumentene som ligger nedlastet paa telefonen.
   *
   * RADEN ER REGISTERET, FILA LIGGER I OPFS. Se state/dokumentlager.ts.
   * Delingen er med vilje: naar produksjonen passerer sluttdatoen, slettes
   * radene i samme transaksjon som loggen — og filene fjernes rett etter,
   * med serie-id-ene vi nettopp leste ut. Da finnes det ett sted som
   * bestemmer hva som skal bort.
   *
   * `series_id` er noekkelen, ikke `id`: det er ÉN nedlastet versjon per
   * serie. Kommer en nyere, erstatter den den gamle.
   */
  await sql`CREATE TABLE IF NOT EXISTS document_cache (
    series_id TEXT PRIMARY KEY, production_id TEXT NOT NULL,
    kind TEXT NOT NULL, title TEXT NOT NULL, shoot_day TEXT NOT NULL DEFAULT '',
    version INTEGER NOT NULL, uploaded_at TEXT NOT NULL,
    file_name TEXT NOT NULL, cached_at INTEGER NOT NULL,
    document_id TEXT, shoot_date TEXT NOT NULL DEFAULT '',
    ulest INTEGER NOT NULL DEFAULT 0)`;

  await sql`CREATE INDEX IF NOT EXISTS document_cache_prod
    ON document_cache (production_id)`;

  /*
   * clock_sample har INGEN produksjonsnoekkel, og slettes ikke med en
   * produksjon. Den maaler enhetens klokke, ikke produksjonens innhold:
   * tallene er skjevhet i millisekunder, og det er ingenting personlig i
   * dem. Drifttesten i fase 1 ville vaert verdiloes om maalingene forsvant
   * med hver produksjon. Se docs/10-slagplan.md.
   */
  await sql`CREATE TABLE IF NOT EXISTS clock_sample (
    id TEXT PRIMARY KEY, at_utc INTEGER NOT NULL, skew_ms REAL NOT NULL,
    quality TEXT NOT NULL)`;

  await oppgrader(aktivProduksjon);
}

/**
 * Nummererte migreringer.
 *
 * HVORFOR `PRAGMA user_version` OG IKKE try/catch SOM FOER. Moensteret over
 * — `ALTER TABLE ... ADD COLUMN` med feilen svelget — virker fordi en ALTER
 * som alt er kjoert kaster, og det er trygt aa proeve igjen. Men migrering 1
 * bygger `user_page_preference` paa NYTT, fordi SQLite ikke kan endre en
 * primaernoekkel. En ombygging som kjoerer to ganger er ikke idempotent, og
 * try/catch kan ikke uttrykke «alt gjort». Derfor en teller.
 *
 * Hvert ledd faar produksjonen appen staar i naa, til aa tilordne rader som
 * fantes foer tabellene hadde en produksjonsnoekkel.
 */
const MIGRERINGER: readonly ((aktivProduksjon: string) => Promise<void>)[] = [
  /* --- 1: produksjonsnoekkel paa de lokale tabellene -------------------
   *
   * Den lokale basen antok én produksjon. `anchorForDay` og `sessionForDay`
   * filtrerte bare paa opptaksdag, saa to produksjoner paa dag 7 ville delt
   * anker og loggoekt — to produksjoners poster i samme logg, under samme
   * tidskode.
   */
  async (aktivProduksjon) => {
    for (const tabell of AVGRENSEDE_TABELLER) {
      /*
       * ALTER kopierer ingen rader. Det er med vilje: en ombygging av
       * log_entry er mer risiko enn oppgaven trenger, og loggen er det vi
       * aldri har raad til aa miste.
       */
      try {
        await raw(`ALTER TABLE ${tabell} ADD COLUMN production_id TEXT NOT NULL DEFAULT ''`);
      } catch { /* kolonnen fantes */ }
      // Radene som laa der foer hoerer til produksjonen appen sto i.
      await raw(`UPDATE ${tabell} SET production_id = '${aktivProduksjon}' WHERE production_id = ''`);
      await raw(`CREATE INDEX IF NOT EXISTS ${tabell}_prod ON ${tabell} (production_id)`);
    }

    /*
     * `user_page_preference` hadde `page` som primaernoekkel. Sidevalg er per
     * produksjon, ikke per bruker — det foelger av at prodleder kan legge til
     * et felt som «vises» til brukeren selv skjuler det. Primaernoekkelen maa
     * derfor bli (production_id, page), og det kan bare gjoeres ved aa bygge
     * tabellen paa nytt.
     */
    await transaction(async (tx) => {
      await tx.sql`CREATE TABLE IF NOT EXISTS user_page_preference_ny (
        production_id TEXT NOT NULL, page TEXT NOT NULL,
        enabled INTEGER NOT NULL, sort_order INTEGER NOT NULL,
        PRIMARY KEY (production_id, page))`;
      await tx.sql([`INSERT OR IGNORE INTO user_page_preference_ny
        (production_id, page, enabled, sort_order)
        SELECT '${aktivProduksjon}', page, enabled, sort_order
        FROM user_page_preference`] as unknown as TemplateStringsArray);
      await tx.sql`DROP TABLE user_page_preference`;
      await tx.sql`ALTER TABLE user_page_preference_ny RENAME TO user_page_preference`;
    });
  },

  /* --- 2: nedlastede dokumenter -----------------------------------------
   *
   * Tabellen staar ogsaa i grunnskjemaet over, saa en ny base faar den med
   * én gang. Dette leddet finnes for basene som alt ligger paa en telefon.
   */
  async () => {
    await raw(`CREATE TABLE IF NOT EXISTS document_cache (
      series_id TEXT PRIMARY KEY, production_id TEXT NOT NULL,
      kind TEXT NOT NULL, title TEXT NOT NULL,
      version INTEGER NOT NULL, uploaded_at TEXT NOT NULL,
      file_name TEXT NOT NULL, cached_at INTEGER NOT NULL)`);
    await raw(`CREATE INDEX IF NOT EXISTS document_cache_prod
      ON document_cache (production_id)`);
  },

  /* --- 3: opptaksdag paa de nedlastede dokumentene -----------------------
   *
   * Appen hentet foer bare dagens dokumenter. Naa beholdes ogsaa tidligere
   * dager — én versjon per dag, den siste — fordi de brukes naar timene
   * dobbeltsjekkes i ettertid. Da maa raden vite hvilken dag den gjelder.
   *
   * Serien er allerede noekkelen, og en serie ER (produksjon, slag, dag), saa
   * «siste versjon per dag» faller ut av seg selv.
   */
  async () => {
    try {
      await raw(`ALTER TABLE document_cache ADD COLUMN shoot_day TEXT NOT NULL DEFAULT ''`);
    } catch { /* kolonnen fantes */ }
  },

  /* --- 4: SU-/synkspoersmaal ---------------------------------------------
   *
   * To nye tabeller, og to kolonner paa loggposten.
   *
   * KOLONNENE ER HELE POENGET. Avhukingen er ikke en egen tilstand noe sted:
   * et spoersmaal er stilt til en deltaker hvis det finnes en LEVENDE post
   * for det paret i denne loggen. Da gaar koblingen begge veier av seg selv
   * — sletter regi posten fra Logg-skjermen, forsvinner haken — og det
   * finnes ikke to steder som kan komme i utakt.
   *
   * ALTER og ikke ombygging: loggen er det vi aldri har raad til aa miste.
   */
  async () => {
    await raw(`CREATE TABLE IF NOT EXISTS participant (
      id TEXT PRIMARY KEY, production_id TEXT NOT NULL,
      name TEXT NOT NULL, abbreviation TEXT NOT NULL, sort_order INTEGER NOT NULL DEFAULT 0)`);
    await raw(`CREATE INDEX IF NOT EXISTS participant_prod ON participant (production_id)`);
    await raw(`CREATE TABLE IF NOT EXISTS su_question (
      id TEXT PRIMARY KEY, production_id TEXT NOT NULL,
      participant_id TEXT, text TEXT NOT NULL, sort_order INTEGER NOT NULL DEFAULT 0)`);
    await raw(`CREATE INDEX IF NOT EXISTS su_question_prod ON su_question (production_id)`);
    for (const kolonne of ["question_id", "participant_id"]) {
      try {
        await raw(`ALTER TABLE log_entry ADD COLUMN ${kolonne} TEXT`);
      } catch { /* kolonnen fantes */ }
    }
  },

  /* --- 5: én post per synk ------------------------------------------------
   *
   * RETTING av ledd 4. Der fikk hvert avhuket spoersmaal sin egen loggpost.
   * Det er feil bilde av hva som skjedde: en synk er ÉN hendelse i
   * materialet, og tjue poster paa tjue sekunder gjoer loggen uleselig.
   *
   * Naa settes ett merke med etiketten SYNK, spoersmaalene legges i teksten,
   * og hvert avhuket spoersmaal faar en rad her.
   *
   * `log_entry.question_id` BLIR STAAENDE, ubrukt. Aa fjerne en kolonne i
   * SQLite betyr aa bygge tabellen paa nytt, og loggen er det vi aldri har
   * raad til aa miste — den risikoen er stoerre enn skaden av en tom
   * kolonne. I Postgres er den droppet; se migrasjonen med samme dato.
   */
  async () => {
    await raw(`CREATE TABLE IF NOT EXISTS su_answer (
      id TEXT PRIMARY KEY, production_id TEXT NOT NULL, entry_id TEXT NOT NULL,
      question_id TEXT NOT NULL, participant_id TEXT NOT NULL, at_utc INTEGER NOT NULL)`);
    await raw(`CREATE INDEX IF NOT EXISTS su_answer_entry ON su_answer (entry_id)`);
    await raw(`CREATE INDEX IF NOT EXISTS su_answer_prod ON su_answer (production_id)`);
  },

  /* --- 6: tekstflater ----------------------------------------------------
   *
   * Brief for teknisk crew er ikke en opplastet PDF. Den skrives paa
   * nettflata og leses som tekst her.
   */
  async () => {
    await raw(`CREATE TABLE IF NOT EXISTS text_page (
      id TEXT PRIMARY KEY, production_id TEXT NOT NULL, slug TEXT NOT NULL,
      title TEXT NOT NULL, body TEXT NOT NULL DEFAULT '', updated_at TEXT NOT NULL)`);
    await raw(`CREATE UNIQUE INDEX IF NOT EXISTS text_page_slug
      ON text_page (production_id, slug)`);
  },

  /* --- 7: rik tekst i beskjedfeltene -------------------------------------
   *
   * `body` var ren tekst. Feltene har naa overskrifter, punkter og merker,
   * og innholdet lagres som et dokument — se packages/shared/src/tekst.
   *
   * Tabellen bygges om og ikke utvides: den er et SPEIL av det som ligger i
   * Supabase, og hentes i sin helhet ved hver synk. Her finnes ingenting aa
   * miste, og det er den eneste tabellen i denne basen det gjelder.
   */
  async () => {
    await raw(`DROP TABLE IF EXISTS text_page`);
    await raw(`CREATE TABLE text_page (
      id TEXT PRIMARY KEY, production_id TEXT NOT NULL, slug TEXT NOT NULL,
      title TEXT NOT NULL, content TEXT NOT NULL DEFAULT '[]', updated_at TEXT NOT NULL)`);
    await raw(`CREATE UNIQUE INDEX IF NOT EXISTS text_page_slug
      ON text_page (production_id, slug)`);
  },

  /* --- 8: utboks ---------------------------------------------------------
   *
   * Foerste gang appen skriver til delt tilstand. Se tabellen i grunnskjemaet.
   */
  async () => {
    await raw(`CREATE TABLE IF NOT EXISTS utboks (
      production_id TEXT NOT NULL, slug TEXT NOT NULL, laget INTEGER NOT NULL,
      PRIMARY KEY (production_id, slug))`);
  },

  /* --- 9: shotlista blir egne tabeller -----------------------------------
   *
   * Den laa i `text_page` som ett dokument. Et bilde skal kunne ha en
   * kommentar og et referansebilde, og det er data per linje. Raden i
   * `text_page` ryddes bort — innholdet var proeving.
   */
  async () => {
    await raw(`CREATE TABLE IF NOT EXISTS shot_scene (
      id TEXT PRIMARY KEY, production_id TEXT NOT NULL, shoot_day TEXT NOT NULL,
      name TEXT NOT NULL, sort_order INTEGER NOT NULL DEFAULT 0)`);
    await raw(`CREATE TABLE IF NOT EXISTS shot (
      id TEXT PRIMARY KEY, scene_id TEXT NOT NULL, text TEXT NOT NULL,
      note TEXT NOT NULL DEFAULT '', image_path TEXT,
      done INTEGER NOT NULL DEFAULT 0, sort_order INTEGER NOT NULL DEFAULT 0)`);
    await raw(`CREATE TABLE IF NOT EXISTS utboks_shot (
      shot_id TEXT PRIMARY KEY, production_id TEXT NOT NULL, laget INTEGER NOT NULL)`);
    await raw(`DELETE FROM text_page WHERE slug = 'shotlist'`);
  },

  /* --- 10: loggen skal opp ------------------------------------------------
   *
   * Loggen har aldri forlatt telefonen. Naa foelger redaktoeren den mens
   * opptaket gaar, og da maa postene opp etter hvert som de skrives.
   *
   * SAMME UTBOKS-MOENSTER som beskjedene og shotlista: det skrives lokalt
   * foerst, alltid, og raden legges i koe. `loggsynk.send` tar den naar
   * nettet er der. Uten koe maatte vi gjettet hva som hadde kommet fram.
   *
   * ETTERFYLLER DET SOM ALT LIGGER HER. En logg skrevet foer dette fantes
   * er like mye logg. Private oekter holdes utenfor — de hoerer ingen
   * produksjon til og skal ingen vei.
   */
  async () => {
    await raw(`CREATE TABLE IF NOT EXISTS utboks_logg (
      tabell TEXT NOT NULL, id TEXT NOT NULL, production_id TEXT NOT NULL,
      laget INTEGER NOT NULL, PRIMARY KEY (tabell, id))`);
    for (const tabell of ["tc_anchor", "log_session", "log_entry"]) {
      await raw(`INSERT OR IGNORE INTO utboks_logg (tabell, id, production_id, laget)
        SELECT '${tabell}', id, production_id, ${Date.now()}
        FROM ${tabell} WHERE production_id <> '' AND production_id <> 'privat'`);
    }
  },

  /* --- 11: det redaktoeren legger igjen ----------------------------------
   *
   * En kommentar, en markering eller et synkspoersmaal skrevet ut av en
   * post. Det hoerer til posten, men det er IKKE loggen — derfor en egen
   * tabell, akkurat som i Postgres. Regi skal kunne se det uten nett, saa
   * det ligger lokalt som alt annet.
   *
   * Hele lista byttes ved henting, som synkspoersmaalene: en kommentar
   * redaktoeren har angret paa skal forsvinne her ogsaa.
   */
  async () => {
    await raw(`CREATE TABLE IF NOT EXISTS entry_note (
      id TEXT PRIMARY KEY, entry_id TEXT NOT NULL, production_id TEXT NOT NULL,
      slag TEXT NOT NULL, tekst TEXT NOT NULL DEFAULT '',
      forfatter TEXT NOT NULL DEFAULT '', laget INTEGER NOT NULL DEFAULT 0)`);
    await raw(`CREATE INDEX IF NOT EXISTS entry_note_post ON entry_note (entry_id)`);
  },

  /* --- 12: rapporten skal opp -------------------------------------------
   *
   * Rapporten ble laget lokalt og delt gjennom systemets delingsark. Naa
   * leveres den til PRODUKSJONEN, og redaktoeren finner den paa nettflata.
   *
   * Koen holder metadataene; selve PDF-en ligger i OPFS til den er oppe, og
   * slettes da. Vi tar vare paa FILA og ikke oppskriften med vilje: det er
   * den som ble levert, og en ny generering senere kunne gitt et annet
   * dokument om loggen var rettet i mellomtiden.
   *
   * Oppsummeringen krypteres, som all annen fritekst. Se state/krypto.ts.
   */
  async () => {
    await raw(`CREATE TABLE IF NOT EXISTS utboks_rapport (
      id TEXT PRIMARY KEY, production_id TEXT NOT NULL, session_id TEXT NOT NULL,
      versjon INTEGER NOT NULL, filnavn TEXT NOT NULL, meta TEXT NOT NULL,
      oppsummering TEXT NOT NULL DEFAULT '', lukket INTEGER NOT NULL,
      laget INTEGER NOT NULL)`);
  },

  /* --- 13: svaret skrives ned, og haken skal opp ------------------------
   *
   * `su_answer` har hatt hake og ingen tekst, og den har vaert BARE LOKAL:
   * ingen har lastet den opp. Haken har derfor aldri naadd redaktoeren
   * annet enn gjennom rapporten, og svaret fanns ikke noe sted.
   *
   * Reporteren skriver svar fra nettflata, og regi skal kunne gjoere det
   * samme i appen. Da maa kolonnen finnes her, og raden maa i utboksen.
   *
   * Svaret krypteres IKKE lokalt, av samme grunn som loggteksten ikke er
   * det i Postgres: det er skjemaets eget valg. TODO ved fase 5 — se
   * state/krypto.ts for hva den lokale krypteringen beskytter mot.
   */
  async () => {
    /* SQLite kaster om kolonnen finnes. En base som alt har den er en base
       som er laget av `opprett`, og da er dette trinnet ingenting. */
    try {
      await raw(`ALTER TABLE su_answer ADD COLUMN answer TEXT NOT NULL DEFAULT ''`);
    } catch { /* finnes alt */ }
    try {
      await raw(`ALTER TABLE su_answer ADD COLUMN answer_at INTEGER`);
    } catch { /* finnes alt */ }
  },

  /* --- 14: spoersmaalet husker hvor det kom fra -------------------------
   *
   * Redaktoeren skriver et synkspoersmaal ut av en loggpost, og klipperen
   * skal kunne finne fram innholdet det hoerer til. Tre ting trengs:
   * tidskoden, posten og rapporten.
   *
   * ALT HER ER EN KOPI AV EN ANNENS LOGG. Posten staar som regel i en annen
   * regissoers oekt, og denne telefonen har aldri sett den — den kan ikke
   * regnes ut lokalt. Derfor hentes den ned med spoersmaalet og legges her,
   * slik at rapporten kan lages uten dekning.
   *
   * Tidskoden som FRAMES, aldri som streng. Justerer regi ankeret, flytter
   * tallet seg ved neste synk.
   */
  async () => {
    for (const [navn, type] of [
      ["kilde_entry", "TEXT"], ["kilde_frames", "INTEGER"], ["kilde_rate", "TEXT"],
      ["kilde_df", "INTEGER"], ["kilde_dag", "TEXT"], ["kilde_tekst", "TEXT"],
      ["kilde_rapport", "INTEGER"],
    ]) {
      try {
        await raw(`ALTER TABLE su_question ADD COLUMN ${navn} ${type}`);
      } catch { /* finnes alt */ }
    }
  },

  /* --- 15: stilt av hvem som helst --------------------------------------
   *
   * Se `su_stilt` i `migrate` for hvorfor dette er en egen tabell og ikke
   * en kolonne paa `su_answer`.
   */
  async () => {
    await raw(`CREATE TABLE IF NOT EXISTS su_stilt (
      production_id TEXT NOT NULL, question_id TEXT NOT NULL,
      participant_id TEXT NOT NULL,
      PRIMARY KEY (production_id, question_id, participant_id))`);
  },

  /* --- 16: én tidskode for hele produksjonen ----------------------------
   *
   * Hver enhet har laget sitt eget anker, og de sa ulike ting: tidskoden i
   * appen stemte ikke med nettflata. Naa adopterer enhetene produksjonens
   * jammede anker og uttrykker verdien paa sin egen klokke.
   *
   * `derived_from` null betyr at noen har JAMMET. Se migrasjonen med samme
   * dato i supabase/migrations.
   */
  async () => {
    try {
      await raw(`ALTER TABLE tc_anchor ADD COLUMN derived_from TEXT`);
    } catch { /* finnes alt */ }
  },

  /* --- 17: pickups og opptaksdagen spoersmaalet ble skrevet paa --------
   *
   * `skrevet` avgjoer om et spoersmaal har staatt for lenge og hoerer i
   * pickups; `shoot_day` er knaggen rapporten baerer. Se migrasjonen med
   * samme dato i supabase/migrations.
   */
  async () => {
    for (const [navn, type] of [["skrevet", "INTEGER"], ["shoot_day", "TEXT"]]) {
      try {
        await raw(`ALTER TABLE su_question ADD COLUMN ${navn} ${type}`);
      } catch { /* finnes alt */ }
    }
  },

  /* --- 18: ankeret sier hva slags anker det er ------------------------
   *
   * `derived_from` null betoed «jam», og da ble ankeret appen lager ved
   * oppstart — «ny logg starter med tidskoden satt til naavaerende
   * klokkeslett» — lest som en jam av alle andre enheter. Regis
   * telefonklokke kunne bli produksjonens tidskode bare ved at appen ble
   * aapnet.
   *
   * ETTERSLEPET SETTES TIL `klokke`, ikke til `jam`, og det er det
   * forsiktige valget: et klokkeanker gjelder bare denne enheten. Er
   * gjetningen feil, er utfallet at enheten adopterer neste ekte jam — ikke
   * at den paatvinger de andre sin egen klokke. De som ER avledet kjennes
   * igjen paa `derived_from`.
   */
  async () => {
    try {
      await raw(`ALTER TABLE tc_anchor ADD COLUMN kind TEXT NOT NULL DEFAULT 'klokke'`);
    } catch { /* finnes alt */ }
    try {
      await raw(`UPDATE tc_anchor SET kind = 'avledet' WHERE derived_from IS NOT NULL`);
    } catch { /* tom tabell */ }
  },

  /* --- 19: de andre paa teamet skriver i samme logg -------------------
   *
   * `skrevet_av` er forkortelsen til den som skrev posten, og den er NULL
   * paa alt man har skrevet selv. En post med forkortelse er hentet NED
   * fra en annen enhet paa teamet: den skal ikke rettes her, og den skal
   * ikke opp i utboksen igjen.
   *
   * Alt som ligger i basen fra foer er ens eget, saa etterslepet er null —
   * og det er riktig uten at vi trenger aa skrive det.
   */
  async () => {
    try {
      await raw(`ALTER TABLE log_entry ADD COLUMN skrevet_av TEXT`);
    } catch { /* finnes alt */ }
  },

  /* --- 20: naar jammen ble satt --------------------------------------
   *
   * Brukeren 01.11.2026: «Hvis tidskoden ikke er jammet av en tekniker de
   * siste 24 timene, saa skal tidskoden vaere roed.»
   *
   * Det spoersmaalet kunne ikke besvares. `wall_utc` er naar DETTE ankeret
   * ble skrevet — og paa et adoptert anker er det naar vi adopterte, ikke
   * naar teknikeren jammet. En jam satt for tretti timer siden, adoptert
   * naa, ville staatt som fersk. Det er feil vei for en advarsel.
   *
   * `jammet_utc` baerer jammens EGEN tid gjennom adopsjonen og gjennom en
   * omstart. Null betyr at ingen har jammet — da er tidskoden foreloepig,
   * og flata viser streker.
   *
   * ETTERSLEPET: et `jam` har sin egen `wall_utc`, og det er riktig svar
   * for dem. Et `avledet` anker fra foer denne migrasjonen vet vi ikke
   * tidspunktet for, og da er `wall_utc` det naermeste vi har — det
   * undervurderer alderen, men bare én gang, og bare paa ankere som alt
   * ligger her.
   */
  async () => {
    try {
      await raw(`ALTER TABLE tc_anchor ADD COLUMN jammet_utc INTEGER`);
    } catch { /* finnes alt */ }
    try {
      await raw(
        `UPDATE tc_anchor SET jammet_utc = wall_utc WHERE kind <> 'klokke'`);
    } catch { /* tom tabell */ }
  },

  /* --- 21: datoen, dokumentraden og om DU har lest den ---------------
   *
   * Brukeren 02.11.2026 ville ha to ting paa dokumentlista: datoen dagen
   * faller paa — «et merke om at denne er til den datoen» — og et merke
   * paa det du ikke har aapnet.
   *
   * `document_id` er dokumentRADEN, ikke serien. Lesekvitteringen henger
   * paa den og paa versjonen: en NY versjon er ulest igjen, fordi det er
   * en ny plan og ikke den gamle om igjen.
   *
   * ETTERSLEPET SETTES TIL LEST. Det som alt ligger paa telefonen er
   * hentet ned for lenge siden, og aa merke hele bunken som ulest ville
   * gjort merket meningsloest den dagen det kom.
   */
  async () => {
    for (const kolonne of [
      "document_id TEXT",
      "shoot_date TEXT NOT NULL DEFAULT ''",
      "ulest INTEGER NOT NULL DEFAULT 0",
    ]) {
      try {
        await raw(`ALTER TABLE document_cache ADD COLUMN ${kolonne}`);
      } catch { /* finnes alt */ }
    }
  },

  /* --- 21: hvem som stilte spoersmaalet, og hvilken dag ---------------
   *
   * Rapporten skiller tre tilstander: stilt her, stilt av en annen, ikke
   * stilt. Brukeren 03.11.2026: «hvis det er noen andre som har synket,
   * men gjort det innen rapporten er levert, saa kommer det opp at
   * spoersmaalet er stilt av (for eksempel Lars)».
   *
   * Bufferen bar bare paret foer. Navnet og dagen kommer ned med samme
   * henting, og de maa ligge lokalt: rapporten lages ofte uten dekning.
   */
  async () => {
    for (const kolonne of ["hvem TEXT", "dag TEXT"]) {
      try {
        await raw(`ALTER TABLE su_stilt ADD COLUMN ${kolonne}`);
      } catch { /* finnes alt */ }
    }
  },
];

async function skjemaversjon(): Promise<number> {
  const rows = await sql<Record<string, unknown>>`PRAGMA user_version`;
  return Number(rows[0]?.["user_version"] ?? 0);
}

async function oppgrader(aktivProduksjon: string): Promise<void> {
  let fra = await skjemaversjon();
  /*
   * En base som er NYERE enn appen roeres ikke. Det skjer naar en eldre
   * fane henger igjen etter en oppdatering, og en nedgradering av skjemaet
   * ville tatt med seg data appen ikke vet om.
   */
  if (fra >= SKJEMAVERSJON) return;
  while (fra < SKJEMAVERSJON) {
    const ledd = MIGRERINGER[fra];
    if (ledd === undefined) break;
    await ledd(aktivProduksjon);
    fra += 1;
    // Settes etter hvert ledd, slik at en avbrutt oppgradering tar igjen
    // der den stoppet og ikke fra begynnelsen.
    await raw(`PRAGMA user_version = ${fra}`);
  }
}

/**
 * Aapner basen og bringer skjemaet opp til dagens versjon.
 *
 * `aktivProduksjon` brukes bare av migreringene, til aa tilordne rader som
 * fantes foer tabellene hadde en produksjonsnoekkel.
 */
export function openDb(aktivProduksjon: string): Promise<void> {
  opened ??= migrate(aktivProduksjon);
  return opened;
}

/**
 * Er loggen faktisk på disk?
 *
 * SQLite faller tilbake til en database i minnet når OPFS ikke er
 * tilgjengelig, og sier bare fra med en advarsel i konsollen. Da ser alt
 * normalt ut gjennom et helt skift, og hele loggen er borte neste gang
 * appen åpnes. Det er den dyreste feilen appen kan gjøre, så den skal
 * oppdages ved oppstart og ikke ved skiftslutt.
 *
 * Vanligste årsak er at tjeneren mangler hodene
 * `Cross-Origin-Opener-Policy: same-origin` og
 * `Cross-Origin-Embedder-Policy: require-corp`.
 */
export async function storageIsPersistent(): Promise<boolean> {
  try {
    const root = await navigator.storage.getDirectory();
    await root.getFileHandle(DB_FILE);
    return true;
  } catch {
    return false;
  }
}

const bool = (v: unknown): boolean => Number(v) === 1;
const nullableNum = (v: unknown): number | null =>
  v === null || v === undefined ? null : Number(v);

// ---- det redaktoeren legger igjen ----------------------------------------

export interface Merknad {
  readonly id: string;
  readonly entryId: string;
  /** «kommentar» | «markering» | «spoersmaal» */
  readonly slag: string;
  readonly tekst: string;
  readonly forfatter: string;
}

export async function lastMerknader(productionId: string): Promise<Merknad[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT * FROM entry_note WHERE production_id = ${productionId} ORDER BY laget ASC`;
  return rows.map((r) => ({
    id: String(r["id"]),
    entryId: String(r["entry_id"]),
    slag: String(r["slag"]),
    tekst: String(r["tekst"] ?? ""),
    forfatter: String(r["forfatter"] ?? ""),
  }));
}

/**
 * Bytter HELE lista for produksjonen.
 *
 * Samme grunn som for synkspoersmaalene: en kommentar redaktoeren har
 * fjernet skal forsvinne fra telefonen ogsaa. Et delvis oppdatert bilde
 * ville latt en angret beskjed bli staaende.
 */
export async function erstattMerknader(
  productionId: string, merknader: readonly Merknad[],
): Promise<void> {
  await transaction(async (tx) => {
    await tx.sql`DELETE FROM entry_note WHERE production_id = ${productionId}`;
    let i = 0;
    for (const m of merknader) {
      await tx.sql`INSERT INTO entry_note (id, entry_id, production_id, slag, tekst,
                                           forfatter, laget)
        VALUES (${m.id}, ${m.entryId}, ${productionId}, ${m.slag}, ${m.tekst},
                ${m.forfatter}, ${i})`;
      i += 1;
    }
  });
}

// ---- utboks for rapporter ------------------------------------------------

export interface Rapportkoe {
  readonly id: string;
  readonly productionId: string;
  readonly sessionId: string;
  readonly versjon: number;
  readonly filnavn: string;
  readonly meta: string;
  readonly oppsummering: string;
  readonly lukketUtcMs: number;
}

export async function koeRapport(r: Rapportkoe): Promise<void> {
  await sql`INSERT OR REPLACE INTO utboks_rapport
    (id, production_id, session_id, versjon, filnavn, meta, oppsummering, lukket, laget)
    VALUES (${r.id}, ${r.productionId}, ${r.sessionId}, ${r.versjon}, ${r.filnavn},
            ${r.meta}, ${await krypter(r.oppsummering)}, ${r.lukketUtcMs}, ${Date.now()})`;
}

export async function rapportkoen(productionId: string): Promise<Rapportkoe[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT * FROM utboks_rapport WHERE production_id = ${productionId}
    ORDER BY laget ASC`;
  return Promise.all(rows.map(async (r) => ({
    id: String(r["id"]),
    productionId: String(r["production_id"]),
    sessionId: String(r["session_id"]),
    versjon: Number(r["versjon"]),
    filnavn: String(r["filnavn"]),
    meta: String(r["meta"]),
    oppsummering: await dekrypter(String(r["oppsummering"] ?? "")),
    lukketUtcMs: Number(r["lukket"]),
  })));
}

export async function rapportkoeKvitter(id: string): Promise<void> {
  await sql`DELETE FROM utboks_rapport WHERE id = ${id}`;
}

/** Neste versjonsnummer for en oekt. Rapporten kan sendes paa nytt. */
export async function nesteRapportversjon(sessionId: string): Promise<number> {
  const rows = await sql<Record<string, unknown>>`
    SELECT MAX(versjon) AS v FROM utboks_rapport WHERE session_id = ${sessionId}`;
  return Number(rows[0]?.["v"] ?? 0) + 1;
}

// ---- utboks for loggen ---------------------------------------------------

/**
 * Legger en rad i koe for opplasting.
 *
 * PRIVAT GAAR INGEN VEI. En privat oekt hoerer ingen produksjon til — det
 * er brukerens egen logg, og det finnes ingen produksjon aa laste den opp
 * til. Vakta staar her, ved doera, og ikke i hvert kallsted.
 *
 * `INSERT OR REPLACE`: en post som endres to ganger foer den kommer fram
 * skal ligge i koe én gang. Det som sendes er alltid det som STAAR lokalt.
 */
async function iKoe(tabell: string, id: string, productionId: string): Promise<void> {
  if (productionId === "" || productionId === "privat") return;
  await sql`INSERT OR REPLACE INTO utboks_logg (tabell, id, production_id, laget)
    VALUES (${tabell}, ${id}, ${productionId}, ${Date.now()})`;
  /*
   * Sier fra at det ligger noe i koen.
   *
   * Uten dette gikk posten opp foerst ved neste takt, og «kontinuerlig
   * mens de skrives» ble opptil fire sekunder etterpaa. Det betyr ogsaa at
   * en post som skrives rett foer telefonen legges bort rekker aa komme
   * fram — etterpaa staar skjermen moerk, og da sover takten.
   *
   * Et vindusbudskap og ikke et kall: lagringen skal ikke kjenne til
   * opplastingen. Samme moenster som `infoflyt:lagringsfeil`.
   */
  window.dispatchEvent(new CustomEvent("infoflyt:loggkoe"));
}

export interface Koerad {
  readonly tabell: string;
  readonly id: string;
}

/** Koen for en produksjon. Ankere foerst, saa oekter, saa poster. */
export async function loggkoen(productionId: string): Promise<Koerad[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT tabell, id FROM utboks_logg WHERE production_id = ${productionId}
    ORDER BY CASE tabell
      WHEN 'tc_anchor' THEN 0 WHEN 'log_session' THEN 1
      WHEN 'log_entry' THEN 2 ELSE 3 END, laget ASC`;
  return rows.map((r) => ({ tabell: String(r["tabell"]), id: String(r["id"]) }));
}

export async function loggkoeKvitter(tabell: string, id: string): Promise<void> {
  await sql`DELETE FROM utboks_logg WHERE tabell = ${tabell} AND id = ${id}`;
}

/** Ett anker, slaatt opp paa id. Trengs naar koen skal sendes. */
export async function anchorById(id: string): Promise<StoredAnchor | null> {
  const rows = await sql<Record<string, unknown>>`
    SELECT * FROM tc_anchor WHERE id = ${id} LIMIT 1`;
  const row = rows[0];
  if (row === undefined) return null;
  return {
    id: String(row["id"]),
    productionId: String(row["production_id"] ?? ""),
    shootDay: String(row["shoot_day"]),
    tcFrames: Number(row["tc_frames"]),
    monotonicMs: Number(row["monotonic_ms"]),
    bootId: String(row["boot_id"]),
    wallUtcMs: Number(row["wall_utc"]),
    framerate: String(row["framerate"]) as StoredAnchor["framerate"],
    dropFrame: bool(row["drop_frame"]),
    source: String(row["source"]) as StoredAnchor["source"],
    derivedFrom: row["derived_from"] == null ? null : String(row["derived_from"]),
    kind: slag(row["kind"], row["derived_from"]),
    jammetUtcMs: row["jammet_utc"] == null ? null : Number(row["jammet_utc"]),
  };
}

/** Én oekt, slaatt opp paa id. */
export async function sessionById(id: string): Promise<LogSession | null> {
  const rows = await sql<Record<string, unknown>>`
    SELECT * FROM log_session WHERE id = ${id} LIMIT 1`;
  const row = rows[0];
  if (row === undefined) return null;
  return {
    id: String(row["id"]),
    productionId: String(row["production_id"]),
    shootDay: String(row["shoot_day"]),
    status: String(row["status"]) as LogSession["status"],
    openedAtUtcMs: Number(row["opened_at"]),
    closedAtUtcMs: nullableNum(row["closed_at"]),
  };
}

/** Én post, slaatt opp paa id. Teksten er dekryptert, som ellers. */
export async function entryById(id: string): Promise<LogEntry | null> {
  const rows = await sql<Record<string, unknown>>`
    SELECT * FROM log_entry WHERE id = ${id} LIMIT 1`;
  const row = rows[0];
  if (row === undefined) return null;
  return {
    id: String(row["id"]),
    productionId: String(row["production_id"]),
    sessionId: String(row["session_id"]),
    anchorId: String(row["anchor_id"]),
    monotonicMs: Number(row["monotonic_ms"]),
    bootId: String(row["boot_id"]),
    wallUtcMs: Number(row["wall_utc"]),
    buttonId: row["button_id"] === null ? null : String(row["button_id"]),
    label: await dekrypter(String(row["label"] ?? "")),
    color: row["color"] === null ? null : String(row["color"]),
    text: await dekrypter(String(row["text"] ?? "")),
    tcState: String(row["tc_state"]) as LogEntry["tcState"],
    deletedAt: nullableNum(row["deleted_at"]),
    participantId: row["participant_id"] == null ? null : String(row["participant_id"]),
    skrevetAv: row["skrevet_av"] == null ? null : String(row["skrevet_av"]),
  };
}

// ---- anker ---------------------------------------------------------------

/**
 * Slaget paa et anker, lest trygt fra en rad.
 *
 * Rader skrevet foer skjemaversjon 18 har ingen verdi, og en tom kolonne
 * skal ikke bli til `jam` ved et uhell — det er nettopp den feilen slaget
 * ble innfoert for aa stoppe. `derived_from` svarer naar kolonnen tier.
 */
function slag(verdi: unknown, avledetAv: unknown): StoredAnchor["kind"] {
  if (verdi === "jam" || verdi === "klokke" || verdi === "avledet") return verdi;
  return avledetAv == null ? "klokke" : "avledet";
}

export async function saveAnchor(a: StoredAnchor): Promise<void> {
  await sql`
    INSERT INTO tc_anchor (id, production_id, shoot_day, tc_frames, monotonic_ms,
                           boot_id, wall_utc, framerate, drop_frame, source,
                           derived_from, kind, jammet_utc)
    VALUES (${a.id}, ${a.productionId}, ${a.shootDay}, ${a.tcFrames}, ${a.monotonicMs},
            ${a.bootId}, ${a.wallUtcMs}, ${a.framerate}, ${a.dropFrame ? 1 : 0}, ${a.source},
            ${a.derivedFrom}, ${a.kind}, ${a.jammetUtcMs})
    ON CONFLICT(id) DO UPDATE SET
      tc_frames = excluded.tc_frames,
      monotonic_ms = excluded.monotonic_ms,
      boot_id = excluded.boot_id,
      wall_utc = excluded.wall_utc,
      derived_from = excluded.derived_from,
      /* En justering gjoer et klokkeanker om til en jam, med samme id.
         Derfor maa slaget kunne skrives over, ikke bare settes én gang. */
      kind = excluded.kind,
      jammet_utc = excluded.jammet_utc`;
  await iKoe("tc_anchor", a.id, a.productionId);
}

/**
 * Lagrer et anker hentet NED fra en annen paa teamet.
 *
 * Det skal ikke i utboksen: det kom ovenfra, og en rad som gaar i ring
 * mellom to enheter er en loekke ingen ser foer koen staar full. Derfor
 * ikke `saveAnchor`, som koeer.
 *
 * Det blir heller ikke enhetens EGET anker. Det ligger her bare for at de
 * andres poster skal kunne regnes ut riktig — posten og ankeret kommer fra
 * samme enhet og samme oppstart, saa regningen er eksakt.
 */
export async function lagreFremmedAnker(a: StoredAnchor): Promise<void> {
  await sql`
    INSERT INTO tc_anchor (id, production_id, shoot_day, tc_frames, monotonic_ms,
                           boot_id, wall_utc, framerate, drop_frame, source,
                           derived_from, kind, jammet_utc)
    VALUES (${a.id}, ${a.productionId}, ${a.shootDay}, ${a.tcFrames}, ${a.monotonicMs},
            ${a.bootId}, ${a.wallUtcMs}, ${a.framerate}, ${a.dropFrame ? 1 : 0}, ${a.source},
            ${a.derivedFrom}, ${a.kind}, ${a.jammetUtcMs})
    ON CONFLICT(id) DO NOTHING`;
}

/**
  * Ankeret for en opptaksdag I EN PRODUKSJON.
  *
  * Produksjonen MAA vaere med. Uten den delte to produksjoner paa dag 7
  * samme anker, og tidskoden i den ene fulgte den andre.
  */
export async function anchorForDay(
  productionId: string, shootDay: string,
): Promise<StoredAnchor | null> {
  const rows = await sql<Record<string, unknown>>`
    SELECT * FROM tc_anchor WHERE production_id = ${productionId}
    AND shoot_day = ${shootDay} ORDER BY wall_utc DESC LIMIT 1`;
  const row = rows[0];
  if (row === undefined) return null;
  return {
    id: String(row["id"]),
    productionId: String(row["production_id"]),
    shootDay: String(row["shoot_day"]),
    tcFrames: Number(row["tc_frames"]),
    monotonicMs: Number(row["monotonic_ms"]),
    bootId: String(row["boot_id"]),
    wallUtcMs: Number(row["wall_utc"]),
    framerate: String(row["framerate"]) as StoredAnchor["framerate"],
    dropFrame: bool(row["drop_frame"]),
    source: String(row["source"]) as StoredAnchor["source"],
    derivedFrom: row["derived_from"] == null ? null : String(row["derived_from"]),
    kind: slag(row["kind"], row["derived_from"]),
    jammetUtcMs: row["jammet_utc"] == null ? null : Number(row["jammet_utc"]),
  };
}

// ---- loggøkt ----------------------------------------------------------

export interface LogSession {
  readonly id: string;
  readonly productionId: string;
  readonly shootDay: string;
  readonly status: "open" | "closed";
  readonly openedAtUtcMs: number;
  readonly closedAtUtcMs: number | null;
}

/**
 * Økta for opptaksdagen.
 *
 * Alt fra samme skift fra samme regi havner i én logg, og den må overleve at
 * appen lukkes og åpnes igjen — ellers er postene borte neste gang regi ser
 * på skjermen. Se docs/05-modul-logg.md.
 *
 * Den nyeste økta for dagen gjenbrukes, også når den er lukket. Om en lukket
 * logg skal kunne ta imot nye markører, eller om det da heller skal bli en ny
 * logg som deler anker, er åpent spørsmål 14 i docs/11-risiko.md. Fram til
 * det er avgjort er det verre å miste poster enn å ta imot én for mye.
 */
export async function sessionForDay(
  productionId: string, shootDay: string, teamId: string | null = null,
): Promise<LogSession> {
  /*
   * ========  ER MAN PAA ET TEAM, ER OEKTA TEAMETS  ========
   *
   * Avgjort 29.10.2026. Hele LET 2 skriver i samme logg, og rapporten blir
   * teamets — den henger paa én `log_session`.
   *
   * ID-EN AVTALES IKKE, DEN REGNES UT. To telefoner i en kjeller kan ikke
   * bli enige om hvilken rad de skriver i, og den som venter paa nett for
   * aa faa vite det kan ikke logge. `oektIdFor` utleder den av
   * (produksjon, dag, team), saa begge lander paa samme rad alene — og
   * `upsert` paa tjeneren blir idempotent.
   *
   * Uten team lages id-en tilfeldig, som foer. En logg uten team har ingen
   * aa dele med.
   */
  const teamets = oektIdFor(productionId, shootDay, teamId);
  if (teamets !== null) {
    const mine = await sql<Record<string, unknown>>`
      SELECT * FROM log_session WHERE id = ${teamets} LIMIT 1`;
    const funnet = mine[0];
    if (funnet !== undefined) {
      return {
        id: String(funnet["id"]),
        productionId: String(funnet["production_id"]),
        shootDay: String(funnet["shoot_day"]),
        status: String(funnet["status"]) as LogSession["status"],
        openedAtUtcMs: Number(funnet["opened_at"]),
        closedAtUtcMs: nullableNum(funnet["closed_at"]),
      };
    }
    const ny: LogSession = {
      id: teamets, productionId, shootDay, status: "open",
      openedAtUtcMs: Date.now(), closedAtUtcMs: null,
    };
    await sql`
      INSERT INTO log_session (id, production_id, shoot_day, status, opened_at, closed_at)
      VALUES (${ny.id}, ${ny.productionId}, ${ny.shootDay},
              ${ny.status}, ${ny.openedAtUtcMs}, ${ny.closedAtUtcMs})`;
    await iKoe("log_session", ny.id, ny.productionId);
    return ny;
  }

  const rows = await sql<Record<string, unknown>>`
    SELECT * FROM log_session WHERE production_id = ${productionId}
    AND shoot_day = ${shootDay}
    ORDER BY opened_at DESC LIMIT 1`;
  const row = rows[0];
  if (row !== undefined) {
    return {
      id: String(row["id"]),
      productionId: String(row["production_id"]),
      shootDay: String(row["shoot_day"]),
      status: String(row["status"]) as LogSession["status"],
      openedAtUtcMs: Number(row["opened_at"]),
      closedAtUtcMs: nullableNum(row["closed_at"]),
    };
  }
  /* Ny oekt. Den legges i koe ved opprettelsen og ikke ved foerste post:
     redaktoeren skal se at en logg er AAPNET, ogsaa foer det staar noe i
     den. En tom spalte med navn paa er et svar; ingen spalte er det ikke. */
  const created: LogSession = {
    id: newId(), productionId, shootDay, status: "open",
    openedAtUtcMs: Date.now(), closedAtUtcMs: null,
  };
  await sql`
    INSERT INTO log_session (id, production_id, shoot_day, status, opened_at, closed_at)
    VALUES (${created.id}, ${created.productionId}, ${created.shootDay},
            ${created.status}, ${created.openedAtUtcMs}, ${created.closedAtUtcMs})`;
  await iKoe("log_session", created.id, created.productionId);
  return created;
}

/**
 * Legger en oekt i loggkoen paa nytt.
 *
 * KOEN KVITTERER ÉN GANG. Oekten legges i koe naar den opprettes og naar den
 * lukkes, og slettes fra koen i det den er kvittert. Mangler raden likevel i
 * basen etterpaa — den gikk opp mens svaret aldri kom fram, eller den ble
 * ryddet bort paa tjeneren — finnes det ingen vei til aa sende den igjen, og
 * da kan rapporten aldri feste seg til den. Da ber rapporten om den.
 */
export async function koeOektPaaNytt(id: string): Promise<void> {
  const oekt = await sessionById(id);
  if (oekt === null) return;
  await iKoe("log_session", id, oekt.productionId);
}

export async function closeSession(id: string, atUtcMs: number): Promise<void> {
  await sql`UPDATE log_session SET status = 'closed', closed_at = ${atUtcMs} WHERE id = ${id}`;
  const oekt = await sessionById(id);
  if (oekt !== null) await iKoe("log_session", id, oekt.productionId);
}

// ---- loggposter ----------------------------------------------------------

/** Append-only. Posten skrives én gang og overskrives aldri i sin helhet. */
/*
 * Krypteringen ligger ved DATABASEGRENSEN, ikke spredt i appen.
 *
 * Resten av koden ser klartekst og merker ingenting. Da finnes det ett sted aa
 * se etter om noe er kryptert, og ett sted det kan gaa galt. Se state/krypto.ts
 * for hva som beskyttes og hva som ikke gjoer det.
 */
export async function insertEntry(e: LogEntry): Promise<void> {
  const [tekst, etikett] = await Promise.all([krypter(e.text), krypter(e.label)]);
  await sql`
    INSERT INTO log_entry (id, production_id, session_id, anchor_id, monotonic_ms,
                           boot_id, wall_utc, button_id, label, color, text,
                           tc_state, deleted_at, participant_id, skrevet_av)
    VALUES (${e.id}, ${e.productionId}, ${e.sessionId}, ${e.anchorId}, ${e.monotonicMs},
            ${e.bootId}, ${e.wallUtcMs}, ${e.buttonId}, ${etikett}, ${e.color}, ${tekst},
            ${e.tcState}, ${e.deletedAt}, ${e.participantId}, ${e.skrevetAv})`;
  /*
   * EN ANNENS POST GAAR IKKE I KOEN.
   *
   * Den kom NED fra tjeneren. La vi den i utboksen, ville enheten sendt
   * den opp igjen — og en rad som gaar i ring mellom to enheter er en
   * loekke ingen ser foer koen staar full.
   */
  if (e.skrevetAv === null) await iKoe("log_entry", e.id, e.productionId);
}

/**
 * Lagrer en post som er hentet NED fra en annen paa teamet.
 *
 * Finnes den fra foer, oppdateres teksten og gravsteinen — den andre kan
 * ha rettet eller fjernet den siden sist. Alt annet staar: tidskoden ble
 * fanget i trykkoeyeblikket paa DERES enhet, og den endres ikke av at vi
 * henter den.
 */
/**
 * Ligger raden i utboksen?
 *
 * Da er den LOKALE kopien fasiten, og en henting fra serveren skal ikke
 * roere den: posten er skrevet eller rettet her og har ikke naadd opp
 * ennaa. Uten denne proeven ville en henting lagt serverens gamle tekst
 * over en rettelse regi nettopp gjorde uten dekning — og utboksen ville
 * sendt den gamle teksten opp igjen etterpaa.
 */
export async function iUtboks(id: string): Promise<boolean> {
  const rader = await sql<Record<string, unknown>>`
    SELECT 1 FROM utboks_logg WHERE id = ${id} LIMIT 1`;
  return rader[0] !== undefined;
}

/** Returnerer sant naar raden faktisk ble skrevet — se `hentFraTeamet`. */
export async function lagreFremmedPost(e: LogEntry): Promise<boolean> {
  const fantes = await sql<Record<string, unknown>>`
    SELECT text, deleted_at FROM log_entry WHERE id = ${e.id} LIMIT 1`;
  const rad = fantes[0];
  if (rad !== undefined) {
    /*
     * SKRIVER BARE NAAR NOE FAKTISK ER ULIKT.
     *
     * Hentingen gaar hvert fjerde sekund og tar naa HELE oekta — ogsaa
     * det man selv skrev paa nettflata. Uten denne proeven ville den
     * skrevet hver eneste post paa nytt, tjue ganger i minuttet, paa en
     * telefon som holder dagen sin i OPFS.
     */
    const samme = await dekrypter(String(rad["text"] ?? "")) === e.text
      && (rad["deleted_at"] == null ? null : Number(rad["deleted_at"])) === e.deletedAt;
    if (samme) return false;
    await sql`
      UPDATE log_entry
         SET text = ${await krypter(e.text)}, deleted_at = ${e.deletedAt}
       WHERE id = ${e.id}`;
    return true;
  }
  await insertEntry(e);
  return true;
}

/** Tekstendring er den ene overskrivingen som finnes. Den revideres. */
export async function updateEntryText(id: string, text: string): Promise<void> {
  await sql`UPDATE log_entry SET text = ${await krypter(text)} WHERE id = ${id}`;
  await koeEtterPost(id);
}

/** Sletting er tombstone, aldri fysisk. */
export async function tombstoneEntry(id: string, atUtcMs: number): Promise<void> {
  await sql`UPDATE log_entry SET deleted_at = ${atUtcMs} WHERE id = ${id}`;
  await koeEtterPost(id);
}

/* Produksjonen staar paa raden, ikke i kallet. Ett oppslag er billigere enn
   et argument som kan vaere feil. */
async function koeEtterPost(id: string): Promise<void> {
  const rows = await sql<Record<string, unknown>>`
    SELECT production_id FROM log_entry WHERE id = ${id} LIMIT 1`;
  const pid = rows[0]?.["production_id"];
  if (pid !== undefined) await iKoe("log_entry", id, String(pid));
}

export async function entriesForSession(sessionId: string): Promise<LogEntry[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT * FROM log_entry WHERE session_id = ${sessionId} ORDER BY wall_utc ASC`;
  return Promise.all(rows.map(async (row) => ({
    id: String(row["id"]),
    productionId: String(row["production_id"]),
    sessionId: String(row["session_id"]),
    anchorId: String(row["anchor_id"]),
    monotonicMs: Number(row["monotonic_ms"]),
    bootId: String(row["boot_id"]),
    wallUtcMs: Number(row["wall_utc"]),
    buttonId: row["button_id"] === null ? null : String(row["button_id"]),
    label: await dekrypter(String(row["label"] ?? "")),
    color: row["color"] === null ? null : String(row["color"]),
    text: await dekrypter(String(row["text"] ?? "")),
    tcState: String(row["tc_state"]) as LogEntry["tcState"],
    deletedAt: nullableNum(row["deleted_at"]),
    participantId: row["participant_id"] == null ? null : String(row["participant_id"]),
    skrevetAv: row["skrevet_av"] == null ? null : String(row["skrevet_av"]),
  })));
}

/** Skriver inn ett dokument og legger sida i utboksen. */
export async function skrivTekstflate(
  productionId: string, slug: string, dok: unknown,
): Promise<void> {
  await transaction(async (tx) => {
    /*
     * `RETURNING` og ikke bare UPDATE.
     *
     * En UPDATE som ikke treffer noen rad er ikke en SQL-feil — den gjoer
     * bare ingenting. Her ville det betydd at en avhuking forsvant i
     * stillhet, og sida likevel havnet i utboksen med det GAMLE innholdet.
     * Det er den verste varianten: ingen feil, bare noe som ble borte.
     *
     * Raden mangler naar sida ikke er synket ned ennaa. Da skal det
     * smelle her, ikke lykkes halvveis.
     */
    const traff = await tx.sql`
      UPDATE text_page SET content = ${JSON.stringify(dok)}
      WHERE production_id = ${productionId} AND slug = ${slug}
      RETURNING slug`;
    if (traff.length === 0) {
      throw new Error(`Ingen tekstflate «${slug}» aa skrive til.`);
    }
    await tx.sql`
      INSERT OR REPLACE INTO utboks (production_id, slug, laget)
      VALUES (${productionId}, ${slug}, ${Date.now()})`;
  });
}

/** Sidene som venter paa aa bli sendt, med innholdet slik det staar naa. */
export async function utboksen(
  productionId: string,
): Promise<{ slug: string; tittel: string; innhold: string }[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT u.slug, t.title, t.content
    FROM utboks u JOIN text_page t
      ON t.production_id = u.production_id AND t.slug = u.slug
    WHERE u.production_id = ${productionId}
    ORDER BY u.laget ASC`;
  return rows.map((r) => ({
    slug: String(r["slug"]),
    tittel: String(r["title"]),
    innhold: String(r["content"] ?? "[]"),
  }));
}

export async function utboksKvitter(productionId: string, slug: string): Promise<void> {
  await sql`
    DELETE FROM utboks WHERE production_id = ${productionId} AND slug = ${slug}`;
}

// ---- deltakere og SU-/synkspoersmaal --------------------------------------

/*
 * Begge er produksjonens og hentes fra Supabase. Her er de bare lagret, slik
 * at de finnes uten dekning. Hentingen staar i state/su.ts.
 *
 * `erstattDeltakere` og `erstattSpoersmaal` bytter HELE lista for
 * produksjonen i én transaksjon. Et spoersmaal redaktoer har fjernet skal
 * forsvinne, og en halvveis oppdatert liste er verre enn en gammel.
 */
export async function lastDeltakere(productionId: string): Promise<Deltaker[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT id, name, abbreviation FROM participant
    WHERE production_id = ${productionId}
    ORDER BY sort_order ASC, abbreviation ASC`;
  return rows.map((r) => ({
    id: String(r["id"]),
    navn: String(r["name"]),
    forkortelse: String(r["abbreviation"]),
  }));
}

export async function erstattDeltakere(
  productionId: string, neste: readonly Deltaker[],
): Promise<void> {
  await transaction(async (tx) => {
    await tx.sql`DELETE FROM participant WHERE production_id = ${productionId}`;
    for (const [i, d] of neste.entries()) {
      await tx.sql`
        INSERT INTO participant (id, production_id, name, abbreviation, sort_order)
        VALUES (${d.id}, ${productionId}, ${d.navn}, ${d.forkortelse}, ${i})`;
    }
  });
}

export async function lastSpoersmaal(productionId: string): Promise<SuSpoersmaal[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT id, participant_id, text, sort_order, skrevet, shoot_day,
           kilde_entry, kilde_frames, kilde_rate, kilde_df, kilde_dag,
           kilde_tekst, kilde_rapport
    FROM su_question
    WHERE production_id = ${productionId}
    ORDER BY sort_order ASC`;
  return rows.map((r) => ({
    id: String(r["id"]),
    deltakerId: r["participant_id"] == null ? null : String(r["participant_id"]),
    tekst: String(r["text"]),
    sortOrder: Number(r["sort_order"]),
    skrevetMs: r["skrevet"] == null ? null : Number(r["skrevet"]),
    opptaksdag: r["shoot_day"] == null ? null : String(r["shoot_day"]),
    kilde: r["kilde_entry"] == null ? null : {
      entryId: String(r["kilde_entry"]),
      tcFrames: Number(r["kilde_frames"] ?? 0),
      framerate: String(r["kilde_rate"] ?? "25"),
      dropFrame: Number(r["kilde_df"] ?? 0) === 1,
      opptaksdag: String(r["kilde_dag"] ?? ""),
      tekst: String(r["kilde_tekst"] ?? ""),
      rapportversjon: r["kilde_rapport"] == null ? null : Number(r["kilde_rapport"]),
    },
  }));
}

export async function erstattSpoersmaal(
  productionId: string, neste: readonly SuSpoersmaal[],
): Promise<void> {
  await transaction(async (tx) => {
    await tx.sql`DELETE FROM su_question WHERE production_id = ${productionId}`;
    /*
     * HER STOD EN TREDJE SLETTING SOM IKKE HOERTE HJEMME, og den var den
     * verste: `DELETE FROM su_answer WHERE production_id = ?`. Hele
     * produksjonens avhukinger ble slettet ved HVER synk av
     * spoersmaalslista — og lista synkes paa hvert slag. Regi huket av, og
     * minuttet etter var haken borte: rapporten viste ingenting som stilt,
     * og ingen fikk se at det skjedde.
     *
     * Det den SKULLE gjoere er det Postgres gjoer med `on delete cascade`
     * fra `su_question`: et svar paa et spoersmaal som ikke finnes lenger
     * har ingenting aa peke paa. Da er det de spoersmaalene som er borte som
     * avgjoer, og ikke produksjonen. Rettet 24.10.2026.
     *
     * HER STOD OGSAA TO SLETTINGER AV `text_page` og `utboks`. De toemte
     * beskjedsidene og utboksen ved samme synk. De hoerer til sletterutinen
     * paa `end_date`, og staar der naa.
     */
    const beholdt = new Set(neste.map((q) => q.id));
    const brukte = await tx.sql<Record<string, unknown>>`
      SELECT DISTINCT question_id FROM su_answer WHERE production_id = ${productionId}`;
    for (const rad of brukte) {
      const qid = String(rad["question_id"]);
      if (beholdt.has(qid)) continue;
      await tx.sql`DELETE FROM su_answer WHERE question_id = ${qid}`;
    }
    for (const q of neste) {
      const k = q.kilde ?? null;
      await tx.sql`
        INSERT INTO su_question (id, production_id, participant_id, text, sort_order,
          skrevet, shoot_day,
          kilde_entry, kilde_frames, kilde_rate, kilde_df, kilde_dag, kilde_tekst,
          kilde_rapport)
        VALUES (${q.id}, ${productionId}, ${q.deltakerId}, ${q.tekst}, ${q.sortOrder},
                ${q.skrevetMs}, ${q.opptaksdag},
                ${k === null ? null : k.entryId}, ${k === null ? null : k.tcFrames},
                ${k === null ? null : k.framerate}, ${k === null ? null : (k.dropFrame ? 1 : 0)},
                ${k === null ? null : k.opptaksdag}, ${k === null ? null : k.tekst},
                ${k === null ? null : k.rapportversjon})`;
    }
  });
}

/*
 * Svarene i én loggoekt.
 *
 * Hentes med posten de hoerer til, slik at «stilt» kan leses som «svaret
 * finnes OG posten lever». Tombstoner er filtrert bort her; i appen ligger
 * de uansett ikke i `entries`.
 */
export async function svarForSession(sessionId: string): Promise<SuSvar[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT a.id, a.entry_id, a.question_id, a.participant_id, a.at_utc,
           a.answer, a.answer_at
    FROM su_answer a
    JOIN log_entry e ON e.id = a.entry_id
    WHERE e.session_id = ${sessionId} AND e.deleted_at IS NULL
    ORDER BY a.at_utc ASC`;
  return rows.map((r) => ({
    id: String(r["id"]),
    entryId: String(r["entry_id"]),
    spoersmaalId: String(r["question_id"]),
    deltakerId: String(r["participant_id"]),
    atUtcMs: Number(r["at_utc"]),
    svar: String(r["answer"] ?? ""),
    svarAtMs: r["answer_at"] === null || r["answer_at"] === undefined
      ? null : Number(r["answer_at"]),
  }));
}

/** Parene som er stilt et sted i produksjonen. Ren hurtigbuffer. */
export async function lastStilt(productionId: string): Promise<string[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT question_id, participant_id FROM su_stilt
    WHERE production_id = ${productionId}`;
  return rows.map((r) => `${String(r["question_id"])}·${String(r["participant_id"])}`);
}

/**
 * Bytter hele bufferen.
 *
 * HELE LISTA BYTTES, som spoersmaalene. Angrer reporteren en hake, skal
 * spoersmaalet tilbake paa regis liste — og da kan ikke en gammel rad bli
 * liggende. Buffer, saa ingenting kan gaa tapt her.
 */
export async function erstattStilt(
  productionId: string,
  par: readonly {
    question: string; participant: string; hvem?: string; dag?: string;
  }[],
): Promise<void> {
  await transaction(async (tx) => {
    await tx.sql`DELETE FROM su_stilt WHERE production_id = ${productionId}`;
    for (const p of par) {
      await tx.sql`
        INSERT OR REPLACE INTO su_stilt
          (production_id, question_id, participant_id, hvem, dag)
        VALUES (${productionId}, ${p.question}, ${p.participant},
                ${p.hvem ?? ""}, ${p.dag ?? ""})`;
    }
  });
}

/**
 * Det samme, med navn og dag. Rapporten trenger begge.
 *
 * `lastStilt` over gir parene som strenger — det er alt FLATA trenger, for
 * der er spoersmaalet enten paa lista eller ikke. Rapporten skiller tre
 * tilstander og maa vite hvem.
 */
export async function lastStiltMedHvem(productionId: string): Promise<readonly {
  question: string; participant: string; hvem: string; dag: string;
}[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT question_id, participant_id, hvem, dag FROM su_stilt
    WHERE production_id = ${productionId}`;
  return rows.map((r) => ({
    question: String(r["question_id"]),
    participant: String(r["participant_id"]),
    hvem: String(r["hvem"] ?? ""),
    dag: String(r["dag"] ?? ""),
  }));
}

export async function lagreSvar(productionId: string, v: SuSvar): Promise<void> {
  await sql`
    INSERT OR REPLACE INTO su_answer
      (id, production_id, entry_id, question_id, participant_id, at_utc,
       answer, answer_at)
    VALUES (${v.id}, ${productionId}, ${v.entryId}, ${v.spoersmaalId},
            ${v.deltakerId}, ${v.atUtcMs}, ${v.svar}, ${v.svarAtMs})`;
  await iKoe("su_answer", v.id, productionId);
}

/**
 * Skriver ned hva de svarte.
 *
 * Raden gaar i koe paa nytt. `INSERT OR REPLACE` paa id oppe gjoer at en
 * rettelse som gaar opp to ganger lander paa samme rad — det er derfor
 * id-ene er klientgenererte.
 */
export async function oppdaterSvar(
  id: string, productionId: string, tekst: string, atMs: number,
): Promise<void> {
  await sql`UPDATE su_answer SET answer = ${tekst}, answer_at = ${atMs} WHERE id = ${id}`;
  await iKoe("su_answer", id, productionId);
}

/** Ett svar, slaatt opp paa id. Trengs naar koen skal sendes. */
export async function svarById(id: string): Promise<SuSvar | null> {
  const rows = await sql<Record<string, unknown>>`
    SELECT id, entry_id, question_id, participant_id, at_utc, answer, answer_at
    FROM su_answer WHERE id = ${id} LIMIT 1`;
  const r = rows[0];
  if (r === undefined) return null;
  return {
    id: String(r["id"]),
    entryId: String(r["entry_id"]),
    spoersmaalId: String(r["question_id"]),
    deltakerId: String(r["participant_id"]),
    atUtcMs: Number(r["at_utc"]),
    svar: String(r["answer"] ?? ""),
    svarAtMs: r["answer_at"] === null || r["answer_at"] === undefined
      ? null : Number(r["answer_at"]),
  };
}

/*
 * Haken tas bort. Raden SLETTES fysisk, og det er ikke i strid med at loggen
 * er append-only: loggposten staar, med tombstone om den ble tom. Dette er
 * bare oppslaget som sier hvilke spoersmaal posten dekker.
 */
export async function slettSvar(id: string): Promise<void> {
  await sql`DELETE FROM su_answer WHERE id = ${id}`;
}

/** Ett spoersmaal lagt til i felt. Resten av lista roeres ikke. */
export async function lagreSpoersmaal(
  productionId: string, q: SuSpoersmaal,
): Promise<void> {
  await sql`
    INSERT OR REPLACE INTO su_question
      (id, production_id, participant_id, text, sort_order, skrevet, shoot_day)
    VALUES (${q.id}, ${productionId}, ${q.deltakerId}, ${q.tekst}, ${q.sortOrder},
            ${q.skrevetMs}, ${q.opptaksdag})`;
}

// ---- tekstflater ---------------------------------------------------------

export async function lastTekstflater(productionId: string): Promise<Tekstflate[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT id, slug, title, content, updated_at FROM text_page
    WHERE production_id = ${productionId}`;
  return rows.map((r) => ({
    id: String(r["id"]),
    slug: String(r["slug"]),
    tittel: String(r["title"]),
    dok: fraLagret(JSON.parse(String(r["content"] ?? "[]")) as unknown),
    endretIso: String(r["updated_at"]),
  }));
}

/** Hele settet byttes. En flate som er fjernet paa nett skal forsvinne. */
/* ---- shotlista ---------------------------------------------------------- */

export interface LokalScene {
  readonly id: string;
  readonly navn: string;
  readonly sortOrder: number;
}

export interface LokaltShot {
  readonly id: string;
  readonly sceneId: string;
  readonly tekst: string;
  readonly notat: string;
  readonly bildeSti: string | null;
  readonly gjort: boolean;
  readonly sortOrder: number;
}

export async function lastShotliste(
  productionId: string, opptaksdag: string,
): Promise<{ scener: LokalScene[]; shots: LokaltShot[] }> {
  const s = await sql<Record<string, unknown>>`
    SELECT id, name, sort_order FROM shot_scene
    WHERE production_id = ${productionId} AND shoot_day = ${opptaksdag}
    ORDER BY sort_order`;
  const scener = s.map((r) => ({
    id: String(r["id"]), navn: String(r["name"]), sortOrder: Number(r["sort_order"]),
  }));
  if (scener.length === 0) return { scener, shots: [] };

  const b = await sql<Record<string, unknown>>`
    SELECT b.id, b.scene_id, b.text, b.note, b.image_path, b.done, b.sort_order
    FROM shot b JOIN shot_scene s ON s.id = b.scene_id
    WHERE s.production_id = ${productionId} AND s.shoot_day = ${opptaksdag}
    ORDER BY b.sort_order`;
  return {
    scener,
    shots: b.map((r) => ({
      id: String(r["id"]),
      sceneId: String(r["scene_id"]),
      tekst: String(r["text"]),
      notat: String(r["note"]),
      bildeSti: r["image_path"] === null ? null : String(r["image_path"]),
      gjort: Number(r["done"]) === 1,
      sortOrder: Number(r["sort_order"]),
    })),
  };
}

export async function erstattShotliste(
  productionId: string, opptaksdag: string,
  scener: readonly LokalScene[], shots: readonly LokaltShot[],
): Promise<void> {
  await transaction(async (tx) => {
    await tx.sql`DELETE FROM shot WHERE scene_id IN (
      SELECT id FROM shot_scene
      WHERE production_id = ${productionId} AND shoot_day = ${opptaksdag})`;
    await tx.sql`DELETE FROM shot_scene
      WHERE production_id = ${productionId} AND shoot_day = ${opptaksdag}`;
    for (const s of scener) {
      await tx.sql`
        INSERT INTO shot_scene (id, production_id, shoot_day, name, sort_order)
        VALUES (${s.id}, ${productionId}, ${opptaksdag}, ${s.navn}, ${s.sortOrder})`;
    }
    for (const b of shots) {
      await tx.sql`
        INSERT INTO shot (id, scene_id, text, note, image_path, done, sort_order)
        VALUES (${b.id}, ${b.sceneId}, ${b.tekst}, ${b.notat}, ${b.bildeSti},
                ${b.gjort ? 1 : 0}, ${b.sortOrder})`;
    }
  });
}

/**
 * Huker av et shot lokalt og legger det i utboksen.
 *
 * Kaster naar raden ikke finnes. Et stille `UPDATE` som ikke traff noe er
 * den verste varianten: haken staar paa skjermen, utboksen sender ingenting,
 * og ingen oppdager det foer noen rigger feil.
 */
export async function settShotGjort(
  productionId: string, shotId: string, gjort: boolean,
): Promise<void> {
  const rader = await sql<Record<string, unknown>>`
    UPDATE shot SET done = ${gjort ? 1 : 0} WHERE id = ${shotId} RETURNING id`;
  if (rader.length === 0) throw new Error(`Ukjent shot: ${shotId}`);
  await sql`
    INSERT OR REPLACE INTO utboks_shot (shot_id, production_id, laget)
    VALUES (${shotId}, ${productionId}, ${Date.now()})`;
}

export async function utboksShots(
  productionId: string,
): Promise<readonly { shotId: string; gjort: boolean }[]> {
  const rader = await sql<Record<string, unknown>>`
    SELECT u.shot_id, b.done FROM utboks_shot u JOIN shot b ON b.id = u.shot_id
    WHERE u.production_id = ${productionId} ORDER BY u.laget`;
  return rader.map((r) => ({
    shotId: String(r["shot_id"]), gjort: Number(r["done"]) === 1,
  }));
}

export async function utboksShotKvitter(shotId: string): Promise<void> {
  await sql`DELETE FROM utboks_shot WHERE shot_id = ${shotId}`;
}

export async function erstattTekstflater(
  productionId: string, neste: readonly Tekstflate[],
): Promise<void> {
  await transaction(async (tx) => {
    await tx.sql`DELETE FROM text_page WHERE production_id = ${productionId}`;
    for (const t of neste) {
      await tx.sql`
        INSERT INTO text_page (id, production_id, slug, title, content, updated_at)
        VALUES (${t.id}, ${productionId}, ${t.slug}, ${t.tittel},
                ${JSON.stringify(t.dok)}, ${t.endretIso})`;
    }
  });
}

// ---- hurtigknapper -------------------------------------------------------

export async function loadButtons(productionId: string): Promise<LogButton[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT * FROM log_button WHERE production_id = ${productionId}
    ORDER BY sort_order ASC`;
  return Promise.all(rows.map(async (row) => ({
    id: String(row["id"]),
    label: await dekrypter(String(row["label"])),
    color: String(row["color"]),
    sortOrder: Number(row["sort_order"]),
  })));
}

/** Hurtigknapper er per bruker per produksjon. Se CLAUDE.md. */
export async function replaceButtons(
  productionId: string, list: readonly LogButton[],
): Promise<void> {
  await transaction(async (tx) => {
    // WHERE, ikke bar DELETE. Uten den tok et knappeoppsett i én
    // produksjon med seg knappene i alle de andre.
    await tx.sql`DELETE FROM log_button WHERE production_id = ${productionId}`;
    for (const b of list) {
      await tx.sql`
        INSERT INTO log_button (id, production_id, label, color, sort_order)
        VALUES (${b.id}, ${productionId}, ${await krypter(b.label)}, ${b.color},
                ${b.sortOrder})`;
    }
  });
}

// ---- sider ---------------------------------------------------------------

export async function loadPagePreferences(
  productionId: string,
): Promise<PagePreference[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT * FROM user_page_preference WHERE production_id = ${productionId}
    ORDER BY sort_order ASC`;
  return rows.map((row) => ({
    page: String(row["page"]) as PagePreference["page"],
    enabled: bool(row["enabled"]),
    sortOrder: Number(row["sort_order"]),
  }));
}

export async function replacePagePreferences(
  productionId: string, list: readonly PagePreference[],
): Promise<void> {
  await transaction(async (tx) => {
    await tx.sql`DELETE FROM user_page_preference WHERE production_id = ${productionId}`;
    for (const p of list) {
      await tx.sql`
        INSERT INTO user_page_preference (production_id, page, enabled, sort_order)
        VALUES (${productionId}, ${p.page}, ${p.enabled ? 1 : 0}, ${p.sortOrder})`;
    }
  });
}

// ---- timeføring ----------------------------------------------------------

export async function loadTimeEntries(productionId: string): Promise<TimeEntry[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT * FROM time_entry WHERE production_id = ${productionId}
    ORDER BY started_at DESC`;
  return Promise.all(rows.map(tilTimeEntry));
}

async function tilTimeEntry(row: Record<string, unknown>): Promise<TimeEntry> {
  return {
    id: String(row["id"]),
    productionId: String(row["production_id"]),
    startedAtUtcMs: Number(row["started_at"]),
    stoppedAtUtcMs: nullableNum(row["stopped_at"]),
    note: await dekrypter(String(row["note"] ?? "")),
    source: String(row["source"]) as TimeEntry["source"],
    monotonicMs: nullableNum(row["monotonic_ms"]),
    bootId: row["boot_id"] === null || row["boot_id"] === undefined
      ? null
      : String(row["boot_id"]),
  };
}

/**
 * Den aapne arbeidstimeren, UANSETT PRODUKSJON.
 *
 * Det ene stedet produksjonsavgrensningen bevisst ikke gjelder. Man kan ikke
 * jobbe paa to produksjoner i samme oeyeblikk, saa det gaar én timer av
 * gangen per person — men man kan jobbe dag paa én og kveld paa en annen.
 * Filtrerte denne paa produksjon, ville et bytte gitt to timere som gikk.
 */
export async function openTimerAnyProduction(): Promise<TimeEntry | null> {
  const rows = await sql<Record<string, unknown>>`
    SELECT * FROM time_entry WHERE stopped_at IS NULL
    ORDER BY started_at DESC LIMIT 1`;
  const row = rows[0];
  return row === undefined ? null : tilTimeEntry(row);
}

export async function saveTimeEntry(t: TimeEntry): Promise<void> {
  await sql`
    INSERT INTO time_entry (id, production_id, started_at, stopped_at, note, source,
                            monotonic_ms, boot_id)
    VALUES (${t.id}, ${t.productionId}, ${t.startedAtUtcMs}, ${t.stoppedAtUtcMs},
            ${await krypter(t.note)}, ${t.source}, ${t.monotonicMs}, ${t.bootId})
    ON CONFLICT(id) DO UPDATE SET
      -- started_at maa med. Uten den ble «Sett tid inn» bare en endring i
      -- minnet, og starttiden falt tilbake ved neste oppstart.
      started_at = excluded.started_at,
      stopped_at = excluded.stopped_at,
      note = excluded.note,
      monotonic_ms = excluded.monotonic_ms,
      boot_id = excluded.boot_id`;
}

export async function clearTimeEntries(productionId: string): Promise<void> {
  await sql`DELETE FROM time_entry WHERE production_id = ${productionId}`;
}

export async function deleteTimeEntry(id: string): Promise<void> {
  await sql`DELETE FROM time_entry WHERE id = ${id}`;
}

// ---- nedlastede dokumenter -----------------------------------------------

export interface LagretDokument {
  readonly serieId: string;
  readonly slag: string;
  readonly tittel: string;
  readonly opptaksdag: string;
  /** Datoen opptaksdagen faller paa, eller tom. */
  readonly dato: string;
  readonly versjon: number;
  readonly lastetOpp: string;
  readonly filnavn: string;
  /** Dokumentraden paa serveren. Lesekvitteringen henger paa den. */
  readonly dokumentId: string | null;
  /** Sann til DU har aapnet denne versjonen. */
  readonly ulest: boolean;
}

export async function lagredeDokumenter(productionId: string): Promise<LagretDokument[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT * FROM document_cache WHERE production_id = ${productionId}`;
  return rows.map((r) => ({
    serieId: String(r["series_id"]),
    slag: String(r["kind"]),
    tittel: String(r["title"]),
    opptaksdag: String(r["shoot_day"] ?? ""),
    dato: String(r["shoot_date"] ?? ""),
    versjon: Number(r["version"]),
    lastetOpp: String(r["uploaded_at"]),
    filnavn: String(r["file_name"]),
    dokumentId: r["document_id"] == null ? null : String(r["document_id"]),
    ulest: Number(r["ulest"] ?? 0) === 1,
  }));
}

/**
 * Merker dokumentet som lest paa DENNE telefonen.
 *
 * Kvitteringen til serveren gaar for seg selv — se `kvitterLest` i
 * state/dokumenter.ts. Denne er den lokale halvdelen, og den maa finnes:
 * merket skal forsvinne i det man aapner dokumentet, ogsaa uten nett.
 */
export async function markerLest(serieId: string): Promise<void> {
  await sql`UPDATE document_cache SET ulest = 0 WHERE series_id = ${serieId}`;
}

/** Registrerer en nedlastet versjon. Erstatter den forrige for samme serie. */
export async function lagreDokument(
  productionId: string, d: LagretDokument,
): Promise<void> {
  await sql`
    INSERT INTO document_cache (series_id, production_id, kind, title, shoot_day,
                                version, uploaded_at, file_name, cached_at,
                                document_id, shoot_date, ulest)
    VALUES (${d.serieId}, ${productionId}, ${d.slag}, ${d.tittel}, ${d.opptaksdag},
            ${d.versjon}, ${d.lastetOpp}, ${d.filnavn}, ${Date.now()},
            ${d.dokumentId}, ${d.dato}, ${d.ulest ? 1 : 0})
    ON CONFLICT(series_id) DO UPDATE SET
      kind = excluded.kind,
      title = excluded.title,
      shoot_day = excluded.shoot_day,
      version = excluded.version,
      uploaded_at = excluded.uploaded_at,
      file_name = excluded.file_name,
      cached_at = excluded.cached_at,
      document_id = excluded.document_id,
      shoot_date = excluded.shoot_date,
      ulest = excluded.ulest`;
}

// ---- revisjon ------------------------------------------------------------

/*
 * `before` og `after` krypteres ogsaa.
 *
 * Revisjonsloggen inneholder den FORRIGE og den nye teksten naar regi retter
 * en loggpost. Krypterte vi bare posten, laa den opprinnelige formuleringen i
 * klartekst rett ved siden av — og da var krypteringen et skalkeskjul.
 */
/**
 * Naar det ble levert en rapport for en oekt.
 *
 * ========  HVORFOR DETTE MAA VISES I LOGGEN  ========
 *
 * Brukeren 02.11.2026: «Hvis en rapport blir levert, saa maa det merkes
 * som en egen linje i loggen, slik at om du fortsetter aa skrive i
 * loggen, saa kan man se at det ikke er med i rapporten som er levert.»
 *
 * Rapporten er et OEYEBLIKKSBILDE av det telefonen hadde akkurat da —
 * det staar i CLAUDE.md, og det er nettopp derfor knappen er stengt
 * mens noe venter i koen. Men loggen kan skrives videre etterpaa, og da
 * finnes det to sannheter paa samme dag uten at noe sier fra.
 *
 * ========  INGEN NY RAD I LOGGEN  ========
 *
 * Merket er ikke en loggpost. Loggen er append-only og hver rad har én
 * forfatter; en systemskrevet linje ville vaert en rad uten skriver, og
 * den ville gaatt opp til serveren som om noen hadde skrevet den.
 * Tidspunktet leses derfor av `audit_log`, som alt staar der, og flata
 * tegner en strek.
 *
 * Flere leveringer gir flere merker — «Endre rapporten» lukker paa nytt.
 */
export async function rapportmerker(
  productionId: string, sessionId: string,
): Promise<number[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT created_at FROM audit_log
     WHERE production_id = ${productionId}
       AND entity_id = ${sessionId}
       AND action = 'report_closed'
     ORDER BY created_at ASC`;
  return rows.map((r) => Number(r["created_at"]));
}

export async function audit(
  productionId: string,
  entityType: string,
  entityId: string,
  action: AuditAction,
  before: string | null,
  after: string | null,
): Promise<void> {
  const [f, e] = await Promise.all([
    before === null ? null : krypter(before),
    after === null ? null : krypter(after),
  ]);
  await sql`
    INSERT INTO audit_log (id, production_id, entity_type, entity_id, action,
                           before, after, created_at)
    VALUES (${newId()}, ${productionId}, ${entityType}, ${entityId}, ${action},
            ${f}, ${e}, ${Date.now()})`;
}

/**
 * Måling av avviket mellom monoton og veggklokkebasert forløpt tid.
 *
 * Drifttesten i fase 1 avgjør om nettleserklokka holder — og den testen er
 * verdiløs uten tall. Se docs/10-slagplan.md.
 */
export async function recordClockSample(skewMs: number, quality: string): Promise<void> {
  await sql`
    INSERT INTO clock_sample (id, at_utc, skew_ms, quality)
    VALUES (${newId()}, ${Date.now()}, ${skewMs}, ${quality})`;
}

export async function clockSamples(): Promise<{ atUtc: number; skewMs: number }[]> {
  const rows = await sql<Record<string, unknown>>`
    SELECT at_utc, skew_ms FROM clock_sample ORDER BY at_utc DESC LIMIT 200`;
  return rows.map((r) => ({ atUtc: Number(r["at_utc"]), skewMs: Number(r["skew_ms"]) }));
}

/**
 * Sletting på produksjonens end_date. Fast regel, ingen timere: den lokale
 * kopien ligger på en privat telefon og skal ikke overleve produksjonen.
 */
export async function wipeIfPastEndDate(
  productionId: string, endDateIso: string | null, todayIso: string,
): Promise<readonly string[] | null> {
  /*
   * Null sluttdato betyr ingen automatisk sletting, og bare «privat» har
   * det. Den private loggen hoerer ikke til en produksjon og kan ikke
   * arve en annens frist. Se Production i types.ts.
   */
  if (endDateIso === null || todayIso <= endDateIso) return null;
  /* Sluttdatoen tar ALT, ogsaa det uleverte. Produksjonen er over, og
     kopien paa en privat telefon skal ikke overleve den. */
  return (await slettProduksjonLokalt(productionId)).serier;
}

/**
 * Den lokale kopien av én produksjon, borte.
 *
 * ========  TO VEIER HIT, SAMME SLETTING  ========
 *
 * Sluttdatoen er den ene: produksjonen er over, og kopien skal ikke
 * overleve den. TAPT TILGANG er den andre — brukeren 01.11.2026: «Hvis
 * noen får sparken, så må vi ha muligheten til å fjerne tilgangen
 * deres.» Serveren nekter fra det sekundet `active_to` er satt, men
 * loggen, dokumentene og deltakerlista ligger på telefonen, og en
 * tilgang som er fjernet alle andre steder er ikke fjernet så lenge
 * den ligger der.
 *
 * ⚠ Det som ikke er synket opp, er tapt. Det er med vilje, og det er
 * den riktige avveiningen: den som er tatt av produksjonen får ikke
 * synke opp lenger uansett — serveren avviser hver rad — så alternativet
 * er ikke «beholdt», det er «liggende på en privat telefon uten at noen
 * kan nå det». Se `tilgang.er_medlem`.
 */
export async function slettProduksjonLokalt(
  productionId: string,
  { beholdUleverte = false }: { beholdUleverte?: boolean } = {},
): Promise<{ serier: readonly string[]; beholdt: number }> {
  /*
   * ========  DET SOM ALDRI KOM FRAM, BLIR LIGGENDE  ========
   *
   * Brukeren 01.11.2026: «Behold uleverte poster lokalt og si fra om
   * dem.»
   *
   * `utboks_logg` er fasiten paa hva som ikke har naadd serveren. De
   * postene er personens eget arbeid, og de finnes ingen andre steder
   * — en sletting her er den eneste slettingen som finnes for dem.
   *
   * OEKTA OG ANKERET FOELGER MED, og det er ikke sjenerositet: en
   * loggpost lagrer `monotonic_ms` og `boot_id`, aldri en tidskode.
   * Uten ankeret kan den ikke regnes ut, og uten oekta henger den
   * ingen steder. Se CLAUDE.md § «Tidskode lagres aldri paa
   * log_entry».
   *
   * Resten gaar: dokumentene, deltakerne, synkspoersmaalene,
   * shotlista. Det er produksjonens materiale, ikke ditt.
   */
  /*
   * ========  DET SOM ALDRI KOM FRAM, BLIR LIGGENDE  ========
   *
   * Brukeren 01.11.2026: «Behold uleverte poster lokalt og si fra om
   * dem.»
   *
   * `utboks_logg` er fasiten paa hva som ikke har naadd serveren. De
   * postene er personens eget arbeid, og de finnes ingen andre steder
   * — en sletting her er den eneste slettingen som finnes for dem.
   *
   * OEKTA OG ANKERET FOELGER MED, og det er ikke sjenerositet: en
   * loggpost lagrer `monotonic_ms` og `boot_id`, aldri en tidskode.
   * Uten ankeret kan den ikke regnes ut, og uten oekta henger den
   * ingen steder. Se CLAUDE.md § «Tidskode lagres aldri paa
   * log_entry».
   *
   * Resten gaar: dokumentene, deltakerne, synkspoersmaalene,
   * shotlista. Det er produksjonens materiale, ikke ditt.
   */

  /*
   * Serie-id-ene leses FOER radene slettes.
   *
   * Filene ligger i OPFS og kjenner ikke til produksjonen; det eneste som
   * knytter dem sammen er disse id-ene. Slettes radene foerst, finnes det
   * ingen vei fra produksjonen til filene, og dokumentene blir liggende paa
   * telefonen etter at produksjonen er over. Det er nettopp det
   * sletterutinen finnes for aa hindre.
   */
  const serier = (await sql<Record<string, unknown>>`
    SELECT series_id FROM document_cache WHERE production_id = ${productionId}`)
    .map((r) => String(r["series_id"]));

  await transaction(async (tx) => {
    /*
     * ÉN produksjon, ikke alle.
     *
     * Her laa den dyreste feilen i fila: seks bare DELETE-setninger. Med
     * flere produksjoner paa telefonen ville den foerste som passerte sin
     * sluttdato tatt med seg alle de andre — og den private loggen, som
     * ingen sluttdato gjelder for.
     *
     * clock_sample staar med vilje utenfor. Den maaler enheten, ikke
     * produksjonen.
     */
    /*
     * Loggen, oekta og ankeret.
     *
     * Rekkefoelgen baerer regelen: postene foerst, og saa spares de
     * oektene og ankrene POSTENE SOM BLE IGJEN peker paa. Det som
     * staar i utboksen er aldri levert, og det blir liggende.
     *
     * Undersetningene gaar mot `utboks_logg` i stedet for en liste
     * bygd i JS — tabellnavn og lister kan ikke vaere parametre, og en
     * streng limt sammen her ville vaert den eneste staden i fila der
     * id-er gikk rett inn i SQL.
     */
    if (!beholdUleverte) {
      await tx.sql`DELETE FROM log_entry   WHERE production_id = ${productionId}`;
      await tx.sql`DELETE FROM log_session WHERE production_id = ${productionId}`;
      await tx.sql`DELETE FROM tc_anchor   WHERE production_id = ${productionId}`;
    } else {
      await tx.sql`DELETE FROM log_entry
         WHERE production_id = ${productionId}
           AND id NOT IN (SELECT id FROM utboks_logg
                           WHERE production_id = ${productionId}
                             AND tabell = 'log_entry')`;
      await tx.sql`DELETE FROM log_session
         WHERE production_id = ${productionId}
           AND id NOT IN (SELECT id FROM utboks_logg
                           WHERE production_id = ${productionId}
                             AND tabell = 'log_session')
           AND id NOT IN (SELECT session_id FROM log_entry
                           WHERE production_id = ${productionId})`;
      await tx.sql`DELETE FROM tc_anchor
         WHERE production_id = ${productionId}
           AND id NOT IN (SELECT id FROM utboks_logg
                           WHERE production_id = ${productionId}
                             AND tabell = 'tc_anchor')
           AND id NOT IN (SELECT anchor_id FROM log_entry
                           WHERE production_id = ${productionId})`;
    }
    await tx.sql`DELETE FROM time_entry WHERE production_id = ${productionId}`;
    await tx.sql`DELETE FROM audit_log WHERE production_id = ${productionId}`;
    await tx.sql`DELETE FROM log_button WHERE production_id = ${productionId}`;
    await tx.sql`DELETE FROM user_page_preference WHERE production_id = ${productionId}`;
    await tx.sql`DELETE FROM document_cache WHERE production_id = ${productionId}`;
    /*
     * Deltakerne foerst: navn paa mennesker foran kamera er det mest
     * sensitive vi har liggende, og de har ingen grunn til aa overleve
     * tilgangen med én dag.
     */
    await tx.sql`DELETE FROM participant WHERE production_id = ${productionId}`;
    await tx.sql`DELETE FROM su_question WHERE production_id = ${productionId}`;
    await tx.sql`DELETE FROM su_answer WHERE production_id = ${productionId}`;
    await tx.sql`DELETE FROM text_page WHERE production_id = ${productionId}`;
    /* UTBOKSEN BLIR STAAENDE naar noe beholdes. Blir personen satt inn
       igjen, gaar postene opp som de skulle; ryddet vi koeen, ville de
       ligget her for alltid uten at noen visste om dem. */
    if (!beholdUleverte) {
      await tx.sql`DELETE FROM utboks WHERE production_id = ${productionId}`;
      await tx.sql`DELETE FROM utboks_logg WHERE production_id = ${productionId}`;
      await tx.sql`DELETE FROM utboks_rapport WHERE production_id = ${productionId}`;
    }
    /*
     * Shotlista tas gjennom scenene: `shot` kjenner bare scenen sin, og
     * uten dette ville bildene blitt liggende uten noe som peker paa dem.
     */
    await tx.sql`DELETE FROM shot WHERE scene_id IN (
      SELECT id FROM shot_scene WHERE production_id = ${productionId})`;
    await tx.sql`DELETE FROM shot_scene WHERE production_id = ${productionId}`;
    await tx.sql`DELETE FROM utboks_shot WHERE production_id = ${productionId}`;
  });
  /* Telles ETTER slettingen: det er det som faktisk ble liggende, ikke
     det vi mente aa spare. */
  const igjen = await sql<Record<string, unknown>>`
    SELECT COUNT(*) AS n FROM log_entry WHERE production_id = ${productionId}`;

  // Den som kalte maa slette filene. Se SessionProvider.
  return { serier, beholdt: Number(igjen[0]?.["n"] ?? 0) };
}
